import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { formatDecimalString } from '@/lib/format';
import { NAV_SURFACE_MODULE } from '@/lib/permissions';
import { CONVERSION_APPROXIMATE_NOTE } from '@/lib/targets';
import { useAuth } from '@/lib/use-auth';
import type {
  BillingRegionDefault,
  BillingRegionFilter,
  RevenueTrackerBandTotal,
  RevenueTrackerBillingRegion,
  RevenueTrackerCycleCell,
  RevenueTrackerList,
  RevenueTrackerRow,
  RevenueTrackerService,
  RevenueTrackerTotalCell,
} from '@/lib/types';
import {
  Button,
  EmptyState,
  Field,
  Input,
  MultiCombobox,
  PageHeader,
  Panel,
  PanelFooter,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import {
  InvoiceNumberLink,
  InvoiceQuickEdit,
} from '@/components/invoices/InvoiceNumberLink';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { ViewToggle as SharedViewToggle } from '@/components/ui/ViewToggle';
import { TrackerSheetPanel } from '@/components/tracker-sheet/TrackerSheetPanel';
import { ExternalTabIcon } from '@/components/tracker-sheet/OpenTrackerSheet';
import {
  RevenueMirrorPanel,
  RevenueSheetPlaceholderPanel,
} from '@/components/revenue-tracker/RevenueMirrorPanel';
import {
  RevenueNotesField,
  useRevenueNotesWriter,
  type RevenueNotesWriter,
} from '@/components/revenue-tracker/RevenueNotesField';
import { useRevenueTrackerSheet } from '@/components/revenue-tracker/use-revenue-tracker-sheet';

/*
 * THE REVENUE TRACKER — a CROSS-TAB, and that is the one thing that makes it
 * structurally different from the two trackers it is modelled on.
 *
 * `/client-deliverables` and `/invoice-tracker` are LISTS: a fixed column set,
 * one row per thing, a `ColumnSpec[]` written in the file. This is a grid.
 * Rows are banded by BILLING REGION and are one per COMPANY; columns are
 * grouped per CYCLE, four to a group — Service Name, Amount, Total, Notes — and
 * **the cycles are DATA**. Three consequences follow and every one of them is a
 * departure from the sibling pages rather than an oversight:
 *
 *  1. **The table's `min-width` is COMPUTED, not a Tailwind literal.** Tailwind
 *     scans SOURCE for class names, so `min-w-[${n}px]` emits no CSS at all and
 *     fails silently — the failure this repo's width rules warn about. The sum
 *     depends on how many cycles came back, which is unknowable at build time,
 *     so it is an inline `style` on a wrapper inside `TableWrap`. See
 *     `tableMinWidthPx`.
 *  2. **A company block is SEVERAL `<tr>`s WITH MERGED CELLS ACROSS THEM.**
 *     Several services in one cycle stack on continuation rows, and the company
 *     name — and each cycle's Total and Notes — is one `rowSpan` cell beside
 *     them, vertically centred, which is the workbook's own shape. `row.key`
 *     keys the BLOCK. ⚠️ *SUPERSEDED: "continuation rows whose company cell is
 *     blank."* Those cells are not blank now, they are ABSENT — see
 *     `CompanyBlock`, where omitting them is what a `rowSpan` requires rather
 *     than an omission.
 *  2b. **THE GRID IS DRAWN FROM CELL BORDERS AND `TR`'s ROW RULE IS SUPPRESSED
 *     EVERYWHERE**, because a row rule crosses a merged cell. The workbook does
 *     the same thing for the same reason — gridlines off, every cell bordered.
 *     See `CELL_RULE` and `NO_ROW_RULE`.
 *  3. **There are no column funnels.** `useTableFilters` keys on a
 *     `ColumnSpec.key` written in the file; there is no such list here, and a
 *     funnel over "Amount in 2025 - 26" is a filter on a cell rather than on a
 *     row. Filtering is the bank above the table instead.
 *
 * ⚠️ **EVERYTHING EXCEPT `Notes` IS DERIVED AND THIS APP NEVER WRITES IT** —
 * the same rule the two sibling trackers run on. See `RevenueNotesField`.
 *
 * ⚠️ **MONEY IS A STRING END TO END.** `formatDecimalString` groups the digits
 * and copies the fraction through verbatim. Nothing here calls `parseFloat`.
 */

/* -------------------------------------------------------------------------
 * Column widths
 * --------------------------------------------------------------------- */

/*
 * 🔴 **WIDTHS ARE DECLARED ON THE HEADER ROW AND MIRRORED AS `max-w` ON THE
 * BODY CELLS.** Owner report against the sibling pages, 2026-09-02: text cut
 * mid-word, one column wrapping to four lines beside squeezed neighbours, and a
 * horizontal scrollbar under columns that were still too narrow. One cause —
 * `Table` carries no `table-fixed`, so with no width asked for anywhere the
 * browser sizes every column off its content and gives the long free-text
 * columns whatever is left.
 *
 * ⚠️ **A `<th>` WIDTH IS A REQUEST, NOT A RULE, AND THAT IS WHY IT WORKS.**
 * Auto table layout honours a declared width unless a cell's MIN-CONTENT demands
 * more. Every free-text cell here is `[overflow-wrap:anywhere]`, whose
 * min-content is one character, so nothing can force a column past what it asks
 * for — the property `table-fixed` would buy, without its cost of clipping the
 * `whitespace-nowrap` money cells.
 *
 * ⚠️ **THE NUMBERS ARE FLOORS SET BY THE HEADER LABEL FIRST.** A column header
 * is `text-2xs` uppercase with `tracking-[0.06em]` plus `TH`'s `px-3` (24px), so
 * "Service Name" needs about 120px before any value is considered. Check the
 * LABEL, then widen for content.
 *
 * ⚠️ **THE `px` NUMBERS AND THE CLASS STRINGS MUST AGREE.** The classes are what
 * the browser reads; the numbers are what `tableMinWidthPx` sums. They are
 * written beside each other for that reason — a class changed without its number
 * puts the table's floor below the sum of its columns, at which point the
 * browser shrinks every column proportionally and the truncation comes straight
 * back.
 */
const COMPANY_PX = 240;
const COMPANY_W = 'w-[240px]';
const COMPANY_MAX_W = 'max-w-[240px]';

const SERVICE_PX = 208;
const SERVICE_W = 'w-[208px]';
const SERVICE_MAX_W = 'max-w-[208px]';

/*
 * ⚠️ **160, NOT 128, SINCE 2026-10-11** — both money columns now carry the
 * italic `incl. GBP 2,800.00 VAT` line under the tax-inclusive figure, and at
 * 128 that line pushed the column wider than the floor `tableMinWidthPx` sums.
 * Widened together with the numbers so the two stay in agreement; the VAT line
 * still wraps rather than overflow for a very large figure.
 */
const AMOUNT_PX = 160;
const AMOUNT_W = 'w-[160px]';

const TOTAL_PX = 160;
const TOTAL_W = 'w-[160px]';

const NOTES_PX = 224;
const NOTES_W = 'w-[224px]';
const NOTES_MAX_W = 'max-w-[224px]';

/*
 * 🔴 **THE RULE BETWEEN CYCLE GROUPS IS A REAL COLUMN, NOT A BORDER.** The
 * workbook separates one cycle's block of columns from the next with a solid
 * dark column about 1.88 characters wide — a column, with a width and a fill,
 * which is why the eye reads the sheet as several tables side by side rather
 * than as one very wide one. A `border-l` cannot reproduce that: a border has no
 * width in the layout the reader perceives, and at 1px it is indistinguishable
 * from the ordinary cell grid it is supposed to outrank.
 *
 * ⚠️ **12px, not the workbook's ~13.2px.** The house spacing grid is 4px and
 * this is decoration, so it is snapped to it. It is a floor like every other
 * number here, and it is counted in `tableMinWidthPx` — a separator that is not
 * counted is a column the table's floor does not pay for, which is the drift the
 * widths section above exists to prevent.
 */
const SEPARATOR_PX = 12;
const SEPARATOR_W = 'w-[12px]';

/** What one cycle's group of four columns asks for, in pixels. */
const CYCLE_GROUP_PX = SERVICE_PX + AMOUNT_PX + TOTAL_PX + NOTES_PX;

/**
 * The table's floor.
 *
 * ⚠️ **AN INLINE `style`, NOT A TAILWIND CLASS, AND THAT IS FORCED RATHER THAN
 * SLOPPY.** Tailwind scans source text for literal class names, so a computed
 * `min-w-[${n}px]` produces no CSS whatsoever and fails silently — leaving the
 * table with no floor at all, which is the exact defect the widths above exist
 * to fix. The cycle count is DATA and cannot be enumerated at build time, so
 * there is no literal to write. A number in a `style` is not a design token and
 * carries no theming concern.
 *
 * ⚠️ **`separatorCount` IS ONE FEWER THAN THE CYCLES, AND `Math.max(0, …)` IS
 * LOAD-BEARING.** Separators sit BETWEEN groups, so a single-cycle table has
 * none and a zero-cycle table must not ask for minus one of them.
 */
function tableMinWidthPx(cycleCount: number): number {
  return (
    COMPANY_PX + CYCLE_GROUP_PX * cycleCount + SEPARATOR_PX * Math.max(0, cycleCount - 1)
  );
}

/**
 * How many `<td>`s one body row holds, and therefore what the band heading row
 * has to span.
 *
 * ⚠️ **DERIVED, NEVER WRITTEN DOWN.** It was `1 + cycles * 4` in two places
 * before the separator column existed, and a band heading that spans the wrong
 * width does not throw — it draws a coloured rule that stops short of the table,
 * or one that pushes an extra empty column onto every band. Both read as a
 * styling accident rather than as an arithmetic one.
 */
function bodyColumnCount(cycleCount: number): number {
  return 1 + cycleCount * 4 + Math.max(0, cycleCount - 1);
}

/* -------------------------------------------------------------------------
 * The frozen company column
 * --------------------------------------------------------------------- */

/*
 * 🔴 **THE COMPANY COLUMN IS FROZEN TO THE LEFT EDGE OF THE SCROLLER.** A
 * cross-tab of four cycles is ~2,990px wide against a content area of about
 * 1,136px at 1440 with the sidebar open, so it scrolls at every width and is
 * MEANT to — the workbook it replaces scrolls too. What it must never do is
 * scroll the company name away, leaving a screen of amounts belonging to
 * nobody.
 *
 * ⚠️ **A STICKY CELL COVERS WHATEVER PASSES UNDER IT, AND HERE THAT IS THE
 * POINT RATHER THAN THE BUG.** This is the FIRST column, so what it covers is
 * content the reader has already scrolled past — ordinary frozen-pane
 * behaviour, recoverable by scrolling back. It is the opposite case to a
 * pinned-RIGHT actions lane, which floats over live cells the reader is trying
 * to read; that is what produced the owner's report of buttons drawn on top of
 * the Comment column, and there is no such lane on this page.
 *
 * Three details are load-bearing, all of them `Table.tsx`'s own rules applied
 * from the outside:
 *
 * - **The hairline is an INSET SHADOW, not a border.** `Table` is
 *   `border-collapse: collapse`, where a border belongs to the table rather
 *   than to the cell: it is painted at the grid line and does NOT travel with a
 *   sticky cell, so a `border-r` here would draw a stray vertical rule down the
 *   middle of the table instead of an edge on the frozen column.
 * - **The background must be opaque**, or the scrolling content reads straight
 *   through. `<tr>` has no background of its own to inherit.
 * - **The frozen cell REDRAWS the row separator** on the last line of a company
 *   block, for the same collapse reason from the other side: a positioned cell
 *   paints above the table's collapsed borders, so `TR`'s `border-b` disappears
 *   underneath it. That is why there are two class strings and not one.
 *
 * ⚠️ `z-20` is the CEILING for page content — the app's sticky top bar sits a
 * rung higher. Do not raise either value to win a fight with something on the
 * page; see the ladder in `src/index.css`.
 *
 * 🔴 **THE COMPANY HEADER IS THE CORNER OF A FROZEN PANE, SO IT STICKS TO THE
 * TOP AS WELL AS THE LEFT, AND IT TAKES THE HIGHEST RUNG (20).** Since
 * 2026-10-11 the table scrolls inside its own viewport-high area (see
 * `TABLE_SCROLL_AREA`), so both header rows freeze to its top. Three rungs, all
 * inside page content: frozen BODY cells 10, the other frozen HEADER cells 15
 * (they cover the frozen company column as rows scroll up beneath them), and
 * this corner 20 (it covers both as the reader scrolls either way).
 */
const FROZEN_TH =
  'sticky left-0 top-0 z-20 bg-surface-sunken shadow-[inset_-1px_0_0_0_var(--hairline),inset_0_-1px_0_0_var(--hairline)]';

/**
 * The non-corner header cells, frozen to the top of the table's scroll area.
 *
 * ⚠️ **AN INSET SHADOW FOR THE RIGHT AND BOTTOM EDGES, ON TOP OF `CELL_RULE`.**
 * A collapsed border belongs to the table and is painted at the grid line, so
 * once the header sticks the borders scroll away with the body and the frozen
 * cells would float with no edges. The shadow travels with the cell; the
 * collapsed border is kept so the unscrolled grid is unchanged. The left edge
 * comes from the neighbour's right edge (or the separator bar).
 *
 * ⚠️ **OPAQUE, AND THE BACKGROUND IS ON THE CELL.** `THead`'s own background
 * paints the `<thead>` box, which does not stick — without one here the body
 * rows read straight through the frozen header.
 *
 * ⚠️ **`top` IS NOT IN THE CLASS.** The first row sits at `top-0`; the second
 * at the first row's MEASURED height, applied as an inline style — see
 * `RevenueTableHead`.
 */
const STICKY_HEADER_CELL =
  'sticky z-[15] bg-surface-sunken shadow-[inset_-1px_0_0_0_var(--hairline),inset_0_-1px_0_0_var(--hairline)]';

/**
 * The lg table's own scroll area: both axes, about the viewport's height.
 *
 * ⚠️ **`relative` IS LOAD-BEARING**, for the reason `TableWrap`'s frozen variant
 * records: without a containing block, the `sr-only` labels inside rows escape
 * the scroll box's clip and stretch the PAGE to the table's full height.
 *
 * ⚠️ **NOT `TableWrap frozenHeader`.** That variant makes every `TH` here take
 * its own `sticky top-0 z-[15]` literal, and this table's header cells already
 * carry positioning and shadow literals of their own — two `z-*` and two
 * `shadow-[…]` utilities on one element resolve by Tailwind's emitted order,
 * not by intent. The 7rem offset leaves the app's top bar and a margin clear.
 */
const TABLE_SCROLL_AREA = 'relative max-h-[calc(100dvh-7rem)] overflow-y-auto';

/**
 * The frozen company cell of one company block.
 *
 * ⚠️ **THERE IS ONLY ONE VARIANT NOW, AND THAT IS A CONSEQUENCE OF THE `rowSpan`
 * RATHER THAN A SIMPLIFICATION.** There used to be two — a mid-block cell
 * drawing only its right edge, and a block-end cell that also redrew the row
 * separator. A real `rowSpan` collapses the whole block's company cells into
 * ONE cell whose bottom edge IS the block's bottom edge, so the mid-block
 * variant became unreachable and was deleted rather than left as a string
 * nothing renders.
 *
 * 🔴 **IT CARRIES NO BACKGROUND, AND THE CALLER MUST SUPPLY AN OPAQUE ONE.**
 * That is why this is a function taking a required argument instead of a
 * constant somebody composes with `cx` and can forget: a sticky cell with a
 * transparent background lets the scrolling amounts read straight through the
 * company name, and a `<tr>` has no background of its own for it to inherit.
 * The band stripe supplies it — see `BAND_TONES`.
 */
function frozenCompanyCell(background: string): string {
  return cx(
    'sticky left-0 z-10 shadow-[inset_-1px_0_0_0_var(--hairline),inset_0_-1px_0_0_var(--hairline)]',
    background,
  );
}

/**
 * The four-sided rule every ordinary cell draws for itself.
 *
 * 🔴 **THE WORKBOOK TURNS GRIDLINES OFF AND BORDERS EVERY CELL INSTEAD**, which
 * is what makes a merged company cell read as one tall box rather than as a
 * column of empty ones. Reproducing it needs the grid drawn from CELLS, and
 * there are two independent reasons it cannot come from `TR` here:
 *
 *  - **A `rowSpan` cell crosses row boundaries.** `TR`'s baked-in `border-b`
 *    would paint a rule straight THROUGH the merged company, Total and Notes
 *    cells — the exact line the merge exists to remove.
 *  - **`TR`'s `last:border-b-0` fires at the end of EVERY BAND, not at the end
 *    of the table**, because each band is its own `<tbody>` and `:last-child`
 *    is scoped to it. So the row rule was already dropping the boundary between
 *    a band's final company and the next band's heading.
 *
 * `border-collapse: collapse` merges the shared edges back to one hairline, so
 * bordering all four sides of every cell costs nothing in weight.
 *
 * ⚠️ **NOT ON THE FROZEN CELL.** A collapsed border belongs to the table and is
 * painted at the grid line, so it does not travel with a sticky cell — see
 * `frozenCompanyCell`, which draws its edges with inset shadows instead.
 */
const CELL_RULE = 'border border-border';

/**
 * The row rule, suppressed — on EVERY row, in the header as well as the body.
 *
 * ⚠️ **AN INLINE `style`, NOT A CLASS, AND THAT IS FORCED.** `TR` bakes in
 * `border-b border-border`, and same-property Tailwind utilities do not resolve
 * by the order they appear in a `class` attribute — so a `border-b-0` passed
 * through `cx` wins or loses depending on the order Tailwind happened to emit
 * them in, which is not a thing to make the table's whole grid depend on. An
 * inline style always wins, needs no knowledge of the emitted stylesheet, and
 * says exactly what it does.
 */
const NO_ROW_RULE = { borderBottomColor: 'transparent' } as const;

/*
 * -------------------------------------------------------------------------
 * The chrome on a TOTAL row
 * -------------------------------------------------------------------------
 *
 * 🔴 **THE WORKBOOK'S `TOTAL` ROW IS THE ONLY YELLOW ONE ON THE SHEET, AND THAT
 * IS NOT REPRODUCIBLE HERE.** `#FFFF00` is one colour in a two-theme app — a
 * shout on white, a glare on the dark canvas — and this repo forbids a literal
 * hex and forbids a `dark:` variant over a token. The semantic translation of
 * *"the row that outranks every row above it"* is a step UP the surface ramp
 * plus a heavier top rule, which reads as weight in both themes and needs no
 * variant because both tokens are defined on both.
 *
 * ⚠️ **AND THE TINT IS NEVER THE SIGNAL.** The house rule is that colour is
 * never the only one; here it could not be even if it were allowed, because the
 * bands above already own the `chart-*` ramp and a fifth band wraps back onto
 * the first. The row says **Total** and **Grand total** in words, in a real
 * `<th scope="row">`, and that is what identifies it — the surface step only
 * separates it from the company blocks it closes.
 *
 * ⚠️ **`surface-sunken` FOR A BAND, `surface-active` FOR THE TABLE.** Two steps
 * of one ramp, so the grand total plainly outranks the band totals without
 * introducing a third colour or a second idea.
 *
 * 🔴 **THE FROZEN VARIANTS ARE WHOLE, SEPARATE LITERALS AND NOT `cx`
 * COMPOSITIONS, BECAUSE `box-shadow` IS ONE PROPERTY.** Two `shadow-[…]`
 * utilities on one element do not merge — one wins, and which one is whatever
 * order Tailwind happened to emit them in, which is the same-property trap
 * `NO_ROW_RULE` above already exists to avoid. The frozen cell needs THREE
 * edges in one declaration: its right edge, its heavier top rule, and the row
 * separator it has to redraw for itself (a collapsed border belongs to the table
 * and is painted at the grid line, so it does not travel with a sticky cell).
 *
 * ⚠️ **THE FROZEN VARIANTS MUST STAY OPAQUE**, hence a `bg-surface-*` and not a
 * tint: a sticky cell with a transparent background lets the scrolling amounts
 * read straight through the word "Total", and a `<tr>` has no background of its
 * own for it to inherit.
 *
 * ⚠️ **`var(--hairline)` AND `var(--hairline-strong)` ARE REAL RUNTIME CUSTOM
 * PROPERTIES** — declared on `:root` and on the dark block in `src/index.css`,
 * not only inside `@theme inline`. A `var(--color-*)` in an arbitrary value
 * would resolve to nothing at runtime, because those exist only to generate
 * utility classes.
 */
const BAND_TOTAL_CELL = 'bg-surface-sunken shadow-[inset_0_1px_0_0_var(--hairline-strong)]';

const BAND_TOTAL_FROZEN =
  'sticky left-0 z-10 bg-surface-sunken shadow-[inset_-1px_0_0_0_var(--hairline),inset_0_1px_0_0_var(--hairline-strong),inset_0_-1px_0_0_var(--hairline)]';

const GRAND_TOTAL_CELL = 'bg-surface-active shadow-[inset_0_2px_0_0_var(--hairline-strong)]';

const GRAND_TOTAL_FROZEN =
  'sticky left-0 z-10 bg-surface-active shadow-[inset_-1px_0_0_0_var(--hairline),inset_0_2px_0_0_var(--hairline-strong),inset_0_-1px_0_0_var(--hairline)]';

/**
 * The dark column between one cycle's group and the next.
 *
 * ⚠️ **A RAW `<td>`, NOT `TD`, AND THAT IS NOT A STYLE PREFERENCE.** `TD` bakes
 * in `px-3 py-2.5`, and this cell is 12px wide — narrower than one side of that
 * padding. Clearing it would mean a `p-0` fighting a baked-in `px-3`, which is
 * the same-property ordering trap `NO_ROW_RULE` above exists to avoid; the house
 * rule against hand-rolling a table cell is about DATA cells, and this one holds
 * none. It is `aria-hidden` and empty for the same reason: a screen reader
 * reading a table cell by cell must not meet a column of silence between every
 * two cycles.
 *
 * ⚠️ **NO BORDER, DELIBERATELY.** With nothing drawing across it the fill runs
 * unbroken from the header to the foot of a band, which is what makes it read as
 * a column rather than as a stack of dark cells.
 *
 * ⚠️ **`hairline-strong`, NOT the workbook's `#666666`.** A literal hex is one
 * colour in a two-theme app: mid grey is a strong rule on white and a muddy
 * smear on the dark canvas. The token is the semantic translation — *the line
 * that outranks the ordinary hairline* — and it is defined on both themes, so no
 * `dark:` variant is written and none is needed.
 */
function CycleSeparator({ rowSpan, header = false }: { rowSpan?: number; header?: boolean }) {
  return (
    <td
      aria-hidden="true"
      {...(rowSpan === undefined ? {} : { rowSpan })}
      /*
       * ⚠️ **In the header it freezes with the header cells**, or the bar
       * scrolls away and leaves a 12px gap in the frozen header that the body
       * rows show through. Same rung as the other frozen header cells.
       */
      className={cx(SEPARATOR_W, 'p-0 bg-hairline-strong', header && 'sticky top-0 z-[15]')}
    />
  );
}

/* -------------------------------------------------------------------------
 * Reading a cell
 * --------------------------------------------------------------------- */

/** What a company shows for a cycle the response said nothing about. */
const EMPTY_CELL: RevenueTrackerCycleCell = {
  services: [],
  total: null,
  totalCurrency: null,
  notes: null,
};

/**
 * One company's answer for one cycle.
 *
 * ⚠️ **THE GRID IS RECTANGULAR: the server sends every cycle on every row**, a
 * cycle with no trade arriving as `{services: [], total: null}` rather than as a
 * missing key — which is what makes every cell a valid place to type a note. So
 * this fallback is a GUARD AGAINST AN OLDER SERVER, not the ordinary path.
 *
 * It still has to exist, and it still has to return a real cell rather than
 * `undefined`: `meta.cycles` names the columns, so a column can legitimately
 * exist for a cycle a given row happens not to carry, and that cell still owes
 * the reader a notes editor — the write is an upsert on the (company, cycle)
 * pair, and a note against an untraded cycle is a real thing to record.
 */
function cellOf(row: RevenueTrackerRow, cycleKey: string): RevenueTrackerCycleCell {
  return row.cycles[cycleKey] ?? EMPTY_CELL;
}

/**
 * The union of the cycles the rows themselves carry, in label order.
 *
 * 🔴 **THIS IS THE FALLBACK, NOT THE ANSWER. `meta.cycles` IS THE ANSWER** — it
 * is ordered by the cycle's START DATE and it includes cycles nobody on this
 * page traded in, and this function can reproduce neither.
 *
 * ⚠️ Two ways it is wrong whenever it is used, both silent:
 *
 *  - **A cycle nobody on this page traded in gets no column here**, so the table
 *    and the spreadsheet mirror show different column sets and nothing says why.
 *  - **The order is a LABEL sort.** `numeric` collation happens to put
 *    `2025 - 26` before `2026 - 27`, and that is a coincidence of the wording
 *    rather than a fact about the cycles — rename one and the columns reorder.
 *
 * It exists so a server that predates `meta.cycles` renders a usable page rather
 * than an empty one. **Do not reach for it while `meta.cycles` is present.**
 */
function cycleKeysOf(rows: readonly RevenueTrackerRow[]): string[] {
  const keys = new Set<string>();
  for (const row of rows) for (const key of Object.keys(row.cycles)) keys.add(key);
  return [...keys].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

/*
 * 🔴 **THE TWO WORDS A WITHHELD TOTAL IS ALLOWED TO SAY, WRITTEN ONCE.** A cell
 * total, a band total and the grand total are withheld for the SAME two reasons
 * and must not develop three vocabularies for them: a reader who learns that
 * `Mixed currencies` means *the server declined to add these* in a cell has to
 * be able to read the same two words closing the band without wondering whether
 * they mean something else. Only the surrounding sentence changes, because only
 * the SUBJECT changes.
 */
const MIXED_CURRENCIES = 'Mixed currencies';
const NO_TOTAL = 'No total';

/**
 * 🔴 **THE UNIT THIS PAGE ASKS FOR — owner's decision, 2026-09-19, and the whole
 * of it is that THE SHEET AND THIS PAGE MUST AGREE.**
 *
 * The Google Sheets mirror reads this same endpoint from a ten-minute worker,
 * and the owner chose to convert it. Converting the sheet ALONE would have left
 * **two views of one dataset disagreeing** — both right, in different units,
 * with nothing on either saying so — so the decision was taken as a pair and
 * this constant is the second half of it.
 *
 * ⛔ **IT MUST EQUAL `MIRROR_BASE_CURRENCY` IN THE BACKEND'S
 * `revenue-tracker-sheet.service.ts`, AND NOTHING ENFORCES THAT.** They are two
 * constants in two repositories and there is no third place to put one: the
 * mirror has no request to carry a currency on — that is why it holds a constant
 * at all — so there is no server-side answer to *"what unit is this dataset
 * reported in"* for both to read. **The agreement is by decision and by these two
 * comments naming each other, which is the weakest kind there is.** Moving one
 * without the other is a `200` on both sides and a finance workbook that no
 * longer matches the screen it mirrors.
 *
 * ── THE COST, STATED BECAUSE IT IS WHAT WAS TRADED AWAY ────────────────────
 *
 * ⚠️ **THIS PAGE STOPS MATCHING XERO LINE FOR LINE.** Until today every figure
 * here was the money the customer was actually billed, in the currency the
 * invoice was raised in, so somebody reconciling a row against the accounts was
 * comparing two copies of one number. They are now comparing a converted figure
 * against a native one. {@link CONVERSION_APPROXIMATE_NOTE} is what tells them,
 * and it is mounted for that reason rather than as decoration.
 *
 * ⛔ **IT IS NOT A PICKER AND MUST NOT BECOME ONE.** A picker here agrees with
 * the sheet only while it happens to be left on this code, which reintroduces
 * the divergence the pairing exists to remove — and it would do so
 * intermittently, per reader, which is worse than doing it always.
 *
 * ⚠️ **SENDING IT IS NOT EVIDENCE IT WAS HONOURED.** A server predating the key
 * ignores it and answers an ordinary `200` of unconverted rows. **Every label on
 * this page reads the ECHO** — see `measuredInCurrency` at the render — and
 * never this constant.
 */
const REPORTED_IN_CURRENCY = 'GBP';

/**
 * The cell's total, or the reason there is not one.
 *
 * 🔴 **`total: null` IS NEVER `0.00`.** It has two causes and both are real
 * answers — the cell holds nothing, or **the cell holds more than one currency
 * and the server withheld the sum**, because adding GBP to USD produces a figure
 * that is wrong with nothing on screen to show it. A reader shown `0.00` where a
 * sum was withheld concludes the company billed nothing that cycle, which is the
 * opposite of the truth.
 *
 * ⚠️ **The two are told apart from the DATA, not from a flag**, because there is
 * none: the services are right there and their currencies either agree or do
 * not. That is the same evidence the server used.
 */
function describeMissingTotal(cell: RevenueTrackerCycleCell): { text: string; title: string } | null {
  if (cell.services.length === 0) return null;
  const currencies = new Set(cell.services.map((service) => service.currency));
  if (currencies.size > 1) {
    return {
      text: MIXED_CURRENCIES,
      title: `This cycle holds ${[...currencies].sort().join(', ')}, so no single total can be stated. The amounts beside each service are exact.`,
    };
  }
  return {
    text: NO_TOTAL,
    title: 'The server states no total for this cycle. The amounts beside each service are exact.',
  };
}

/**
 * The reason a BAND's or the TABLE's total is absent — the same two answers as
 * `describeMissingTotal`, about a larger subject.
 *
 * 🔴 **`currencies` IS EVIDENCE FOR THE SENTENCE AND IS NEVER ARITHMETIC.**
 * Nothing on this page sums anything: the figure is the server's or there is no
 * figure. What the visible rows CAN answer is *why* there is none — their
 * currencies either agree or they do not, which is the same evidence the server
 * weighed, and it is the difference between a reader learning that a sum was
 * declined and a reader learning nothing at all.
 *
 * ⚠️ **AN EMPTY SET IS NOT `No total`, IT IS NOTHING TO TOTAL**, and the caller
 * renders an em dash for it — the same treatment an empty cell already gets. A
 * band that traded nothing in a cycle and a band whose sum was withheld are
 * different facts, and this is the line between them.
 *
 * ⚠️ The set is drawn from the rows ON SCREEN, so it is a true statement about
 * what the reader can see and never a claim about rows they cannot. That holds
 * because the whole totals block is suppressed the moment a filter can remove
 * rows from within a band — see `showTotals`.
 */
function describeMissingAggregate(
  currencies: ReadonlySet<string>,
  wording: { subject: string; scope: string },
): { text: string; title: string } | null {
  if (currencies.size === 0) return null;
  if (currencies.size > 1) {
    return {
      text: MIXED_CURRENCIES,
      title: `${wording.subject} holds ${[...currencies].sort().join(', ')}, so no single total can be stated. The figures in the cells above are exact.`,
    };
  }
  return {
    text: NO_TOTAL,
    title: `The server states no total for ${wording.scope}. The figures in the cells above are exact.`,
  };
}

/**
 * The unit to print beside a band's name.
 *
 * 🔴 **ON A CONVERTED TAB THE BAND'S OWN CONFIGURED CURRENCY IS A FLAT FALSE
 * LABEL.** `billingRegion.currencyCode` is what the band is *configured* to bill
 * in — `Europe (EUR)` — and it is completely untouched by conversion, so left
 * alone it sits over figures now stated in something else. **It is the version
 * somebody writes by converting the money and leaving the layout alone**, and
 * the backend's sheet mirror carries a test for exactly this mistake because it
 * was nearly made there first.
 *
 * ⚠️ **GATED ON THE ECHO, NEVER ON {@link REPORTED_IN_CURRENCY}.** Nothing was
 * converted unless the server says so, and on that response the band's own code
 * is the correct and honest label again.
 */
function bandUnitOf(
  group: { currencyCode: string | null },
  measuredInCurrency: string | null,
): string | null {
  return measuredInCurrency ?? group.currencyCode;
}

/** Every currency the visible rows put in one cycle. See above — evidence, never arithmetic. */
function currenciesIn(rows: readonly RevenueTrackerRow[], cycle: string): Set<string> {
  const currencies = new Set<string>();
  for (const row of rows) {
    for (const service of cellOf(row, cycle).services) currencies.add(service.currency);
  }
  return currencies;
}

/**
 * A figure with its currency beside it.
 *
 * ⚠️ **THE CURRENCY IS NOT DECORATION HERE.** This is a cross-tab of several
 * cycles, so a bare figure invites a reader to add across columns — and a
 * company's bands and cycles need not share a currency. The unit travels with
 * every figure for that reason, on a totals row most of all: that is the row
 * somebody reads across.
 *
 * `emphatic` is the totals rows' extra weight, as a ternary over two LITERAL
 * classes rather than an appended one — `font-medium` and `font-semibold` are
 * the same CSS property, and same-property utilities do not resolve by the order
 * they appear in a `class` attribute.
 */
function TotalFigure({
  total,
  currency,
  emphatic,
  vat,
}: {
  total: string;
  currency: string | null;
  emphatic?: boolean;
  /** The tax inside `total`, already resolved by `taxInclusiveFigure`. */
  vat?: string | null;
}) {
  return (
    <span className="inline-flex flex-col items-end gap-0.5 whitespace-nowrap">
      <span className="inline-flex items-baseline justify-end gap-1">
        {currency !== null && <span className="text-2xs text-fg-subtle">{currency}</span>}
        <span
          className={cx(
            'text-xs text-fg tabular',
            emphatic ? 'font-semibold' : 'font-medium',
          )}
        >
          {formatDecimalString(total)}
        </span>
      </span>
      <VatNote vat={vat ?? null} currency={currency} />
    </span>
  );
}

/*
 * 🔴 **THIS TABLE HEADLINES THE TAX-INCLUSIVE FIGURE — owner's decision,
 * 2026-10-11.** Every money figure on the Revenue Tracker table (a paid line's
 * Amount, a company's cycle Total, a band Total and the Grand total) shows the
 * amount INCLUDING tax, with the tax inside it on an italic muted line beneath.
 * The pre-tax figure is not shown here any more. ⛔ **THIS TABLE ONLY** — the
 * Revenue by Company / Product / Top Movers views stay ex-tax.
 *
 * ⚠️ **THE INCL FIELDS ARE OPTIONAL, AND `undefined` IS AN OLDER API.** The app
 * deploys ahead of the server, so a response with no `amountInclTax` /
 * `totalInclTax` key renders EXACTLY what it rendered before — the ex-tax
 * figure, no VAT line. Never derive a tax-inclusive figure in the browser from
 * the ex-tax one: nothing on this page sums or multiplies money.
 *
 * ⚠️ **`null` FOLLOWS THE CELL'S OWN NULL RULE** — a withheld total stays
 * withheld (a sentence or an em dash), never a zero, and never a fallback to the
 * ex-tax figure under a column that now means something else.
 */

/**
 * Picks the headline and the VAT line for one figure.
 *
 * `inclTax` is the server's tax-inclusive field as received: `undefined` means
 * the server predates it, so `exTax` is the headline and there is no VAT line.
 */
function taxInclusiveFigure(
  exTax: string | null,
  inclTax: string | null | undefined,
  tax: string | null | undefined,
): { headline: string | null; vat: string | null } {
  if (inclTax === undefined) return { headline: exTax, vat: null };
  if (inclTax === null) return { headline: null, vat: null };
  return { headline: inclTax, vat: isPositiveDecimal(tax) ? tax : null };
}

/**
 * Whether a decimal string is strictly above zero — tested on the STRING, never
 * through `Number()`, so a money value never meets a float. A malformed value
 * answers `false`, which renders no VAT line rather than a wrong one.
 */
function isPositiveDecimal(value: string | null | undefined): value is string {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return false;
  return /[1-9]/.test(trimmed);
}

/** `incl. GBP 2,800.00 VAT` — a fact about the figure above it, shown only when tax is non-zero. */
function VatNote({ vat, currency }: { vat: string | null; currency: string | null }) {
  if (vat === null) return null;
  return (
    <span className="text-right text-2xs italic whitespace-normal text-fg-muted tabular">
      incl. {currency !== null ? `${currency} ` : ''}
      {formatDecimalString(vat)} VAT
    </span>
  );
}

/** The mark a withheld total gets, in place of a figure that would be a lie. */
function WithheldTotal({ missing }: { missing: { text: string; title: string } }) {
  return (
    <span title={missing.title} className="text-2xs text-fg-muted [overflow-wrap:anywhere]">
      {missing.text}
    </span>
  );
}

function TotalCell({ cell }: { cell: RevenueTrackerCycleCell }) {
  const { headline, vat } = taxInclusiveFigure(cell.total, cell.totalInclTax, cell.totalTax);
  if (headline !== null) {
    return <TotalFigure total={headline} currency={cell.totalCurrency} vat={vat} />;
  }
  const missing = describeMissingTotal(cell);
  if (missing === null) return <NotRecorded />;
  return <WithheldTotal missing={missing} />;
}

/**
 * One cycle's figure on a totals row — the server's number, or why there is
 * none.
 *
 * ⚠️ **`total: null` FALLS THROUGH TO A SENTENCE OR AN EM DASH, NEVER TO A
 * ZERO.** The same rule as a cell, one scale up, and the one that matters most
 * here: a `0.00` closing a band is the most authoritative-looking wrong number
 * this page could print.
 */
function AggregateTotalCell({
  cell,
  rows,
  cycle,
  wording,
}: {
  cell: RevenueTrackerTotalCell;
  rows: readonly RevenueTrackerRow[];
  cycle: string;
  wording: { subject: string; scope: string };
}) {
  const { headline, vat } = taxInclusiveFigure(cell.total, cell.totalInclTax, cell.totalTax);
  if (headline !== null) {
    return <TotalFigure total={headline} currency={cell.totalCurrency} emphatic vat={vat} />;
  }
  const missing = describeMissingAggregate(currenciesIn(rows, cycle), wording);
  if (missing === null) return <NotRecorded />;
  return <WithheldTotal missing={missing} />;
}

/* -------------------------------------------------------------------------
 * Grouping
 * --------------------------------------------------------------------- */

/**
 * What one band paints with.
 *
 * `stripe` and `stripeFrozen` are THE SAME TINT rendered two ways, because the
 * frozen company cell has a constraint no other cell has — see below.
 */
interface BandTone {
  /** The band's own full-width heading row. */
  heading: string;
  /** Every ordinary cell of a STRIPED company block. */
  stripe: string;
  /** The same tint on the frozen company cell, which must also be OPAQUE. */
  stripeFrozen: string;
}

/*
 * 🔴 **A BAND'S TONE IS ITS INDEX INTO THIS ARRAY, MODULO ITS LENGTH — NEVER A
 * MAP KEYED ON THE REGION.** `groupByRegion`'s own note forbids a per-band
 * colour map, and the reason is worth repeating at the array rather than left
 * one screen away: the bands are DATA. Migration 138 added North America as a
 * fourth region and needed no change to this file; a fifth arrives the same way,
 * and a map would answer `undefined` for it — a band rendered with no tint at
 * all, which looks like a plain block rather than like a missing case. The
 * modulo means a fifth band WRAPS to the first tone: two bands sharing a tint is
 * a legible page, and neither the heading nor the currency beside it is
 * ambiguous, because the tint was never the thing carrying the meaning.
 *
 * ⚠️ **`chart-*`, NOT the status families.** A billing region is not a status,
 * and painting one `success` green teaches a reader that the colours here mean
 * something they do not.
 *
 * ⚠️ **THE RAMP IS MONOCHROME — one hue, four steps of lightness and chroma —
 * so these bands are NOT told apart by colour and must never need to be.** The
 * house rule is that colour is never the only signal; here it could not be even
 * if it were allowed. The band's NAME and its CURRENCY are the signal, the tint
 * only groups the rows underneath them, and it is the workbook's own device
 * rather than a legend.
 *
 * ⚠️ **THE PERCENTAGES RUN UP THE RAMP BECAUSE THE RAMP RUNS DOWN.** `chart-1`
 * is the most saturated step and `chart-4` the palest, in both themes — so a
 * single shared alpha would give band one a heavy wash and band four almost
 * nothing. Raising the alpha as the step weakens keeps the four bands within
 * sight of one another in weight. Re-tune the numbers if the ramp is re-tuned;
 * do not flatten them to one value.
 *
 * 🔴 **`stripeFrozen` IS TWO PROPERTIES, NOT TWO BACKGROUNDS FIGHTING.** The
 * frozen cell has to be opaque or the scrolling amounts read through the company
 * name, and a tint is by definition not. Two `bg-<colour>` utilities on one
 * element is the same-property ordering trap this file already documents twice,
 * so the opaque base is `background-color` (`bg-surface`) and the tint is a flat
 * `background-image` gradient over it — different properties, deterministic,
 * every one of them a literal utility Tailwind's scanner can see. It composites
 * to exactly what `stripe` shows over the panel's own `bg-surface`, which is why
 * the two carry the same number and must keep doing so.
 *
 * 🔴 **NEUTRAL SINCE THE 2026-10 UI REWORK — the `chart-*` washes are gone.**
 * *SUPERSEDED: one `chart-1..4` tint per band, alpha rising up the ramp.* The
 * chart palette belongs to charts, and this page has none, so a band now reads
 * as a sunken heading row over neutral company stripes. Every band takes the
 * same tone; the array and the modulo stay so a later per-band treatment is a
 * data change rather than a rewrite. The `stripeFrozen` two-property rule above
 * still holds and is why the frozen stripe is a gradient over `bg-surface`.
 */
const FIRST_BAND_TONE: BandTone = {
  heading: 'bg-surface-sunken',
  stripe: 'bg-surface-sunken/50',
  stripeFrozen: 'bg-surface bg-linear-to-r from-surface-sunken/50 to-surface-sunken/50',
};

const BAND_TONES: readonly BandTone[] = [FIRST_BAND_TONE];

/**
 * The tone for the band at `index`, wrapping past the end of the array.
 *
 * 🔴 **THE ONE PLACE THE MODULO IS WRITTEN.** Both views take their band tone
 * from here — the table and the sub-`lg` cards — so the two cannot answer
 * differently for the same band, which is the whole point of them sharing a
 * palette. Computing it twice worked and would have kept working right up until
 * somebody changed one of them.
 *
 * The `??` is unreachable while `BAND_TONES` has members, and it is written
 * rather than asserted away: an index signature says the lookup can miss, and a
 * `!` silencing it would go on being silent if the array were ever emptied.
 */
function bandTone(index: number): BandTone {
  return BAND_TONES[index % BAND_TONES.length] ?? FIRST_BAND_TONE;
}

/**
 * What one company block paints with — the striped tint, or nothing.
 *
 * ⚠️ **THE PLAIN BLOCK STILL NAMES A FROZEN BACKGROUND.** Its ordinary cells
 * want no paint at all and inherit the panel's surface, but the sticky company
 * cell cannot inherit anything and would go transparent. `''` and `'bg-surface'`
 * are therefore both correct and are not interchangeable.
 */
interface BlockTint {
  cells: string;
  frozen: string;
}

const PLAIN_BLOCK: BlockTint = { cells: '', frozen: 'bg-surface' };

/**
 * The tint for one company block within its band.
 *
 * ⚠️ **STRIPED PER COMPANY BLOCK, NOT PER LINE.** A block is several `<tr>`s
 * whenever a cycle holds several services; striping the lines would cut a
 * company in half and undo what the merged cells are for. The workbook stripes
 * the blocks and so does this.
 *
 * The first block under a heading is PLAIN, so the coloured heading row has a
 * clean edge below it rather than running into a tint of its own family.
 */
function blockTint(tone: BandTone, blockIndex: number): BlockTint {
  return blockIndex % 2 === 1 ? { cells: tone.stripe, frozen: tone.stripeFrozen } : PLAIN_BLOCK;
}

/**
 * A band's identity, from the only field that is one.
 *
 * 🔴 **THE ONE PLACE THIS KEY IS BUILT.** Two things compute it — the grouping
 * of the rows, and the lookup that matches `meta.bandTotals` onto those groups —
 * and if the two ever spelled it differently the lookup would simply miss:
 * every band total would silently vanish, which looks exactly like a server that
 * does not send them. Nothing would fail and nothing would be logged.
 *
 * ⚠️ Keyed on `slug`, never on `name`. A label is editable wording, and this
 * repo has recorded three separate defects caused by a rule keyed on one.
 *
 * ⚠️ **`null` GETS A REAL KEY, NOT AN ABSENCE.** A company that resolves no
 * billing region is an ordinary state with an honest band of its own, so it has
 * a total like any other band.
 */
function bandKeyOf(region: RevenueTrackerBillingRegion | null): string {
  return region ? `r:${region.slug}` : 'none';
}

interface RegionGroup {
  /** The region's slug, or the literal `none` — never the label, which is not unique. */
  key: string;
  label: string;
  /** The band's currency. `null` where the region records none. */
  currencyCode: string | null;
  rows: RevenueTrackerRow[];
}

/**
 * Groups CONSECUTIVE runs of rows by billing region, never by collecting them
 * into a map.
 *
 * The distinction matters and it is the same one the client deliverables
 * tracker makes: the API returns rows already banded, and a map-based grouping
 * would silently REORDER anything that did not arrive that way — so the page
 * would show an arrangement the server never sent, and no filter could explain
 * it. Runs show exactly what came back. If a region appears twice, that is the
 * response, and it is visible rather than papered over.
 *
 * ⚠️ Keyed on `slug`, never on `name`. A label is editable wording, and this
 * repo has recorded three times what a rule keyed on wording does.
 *
 * ⚠️ **THE BANDS ARE DATA AND THERE IS NO LIST OF THEM ANYWHERE IN THIS APP.**
 * Migration 138 adds North America as a fourth alongside United Kingdom, Europe
 * and Rest of the World, and it needed no change here — which is the property to
 * preserve. A hardcoded set of bands, a per-band colour map or a band ORDER
 * written in this file would each have to be edited for a fifth, and a band
 * missing from any of them fails silently rather than loudly.
 *
 * `billingRegion: null` is deliberately labelled neutrally: it does not prove
 * the company has no region, only that none came back on this row.
 */
function groupByRegion(rows: readonly RevenueTrackerRow[]): RegionGroup[] {
  const groups: RegionGroup[] = [];
  for (const row of rows) {
    const key = bandKeyOf(row.billingRegion);
    const last = groups.at(-1);
    if (last && last.key === key) {
      last.rows.push(row);
      continue;
    }
    groups.push({
      key,
      /*
       * ⚠️ **AN HONEST HEADING, NOT A BLANK ONE.** A company can genuinely
       * resolve no billing region — `d_companies.country_id` is nullable and was
       * never backfilled — so this band holds real customers. An empty heading
       * over real rows reads as a rendering fault, and the reader goes hunting a
       * bug in the page instead of a missing country on a company.
       */
      label: row.billingRegion?.name.trim() || 'No billing region recorded',
      currencyCode: row.billingRegion?.currencyCode.trim() || null,
      rows: [row],
    });
  }
  return groups;
}

/* -------------------------------------------------------------------------
 * Cells
 * --------------------------------------------------------------------- */

/** The mark every unset value gets, so an empty cell never reads as a zero. */
function NotRecorded() {
  return <span className="text-fg-subtle">—</span>;
}

/**
 * A service on one line of a company block.
 *
 * ⚠️ **Clamped to two lines with a `title`, not wrapped freely.** A service name
 * is the longest value on the row; an unbounded one sets the height of the whole
 * line — the owner's *"ragged rows"* — while asking for width the money columns
 * beside it then lose. Nothing is unreachable, only abbreviated: the `title` is
 * the route to the rest.
 */
function ServiceNameCell({ service }: { service: RevenueTrackerService }) {
  const name = service.serviceName.trim();
  if (name === '') return <NotRecorded />;
  return (
    <span title={name} className="line-clamp-2 text-xs text-fg-secondary [overflow-wrap:anywhere]">
      {name}
    </span>
  );
}

/**
 * The money on one service line, with the document it was billed on.
 *
 * ⚠️ **The CURRENCY is read off the SERVICE, not off the band.** They agree on
 * every ordinary row — the band's code is the unit for the region — but a
 * mis-filed document can carry another, and rendering the band's code over it
 * would print a figure in a currency it is not in. That is the one error on this
 * page a reader cannot detect for themselves.
 */
function AmountCell({ service }: { service: RevenueTrackerService }) {
  const { headline, vat } = taxInclusiveFigure(service.amount, service.amountInclTax, service.tax);
  const label = service.invoiceNumber?.trim();
  return (
    <span className="flex flex-col items-end gap-0.5 text-xs whitespace-nowrap">
      {headline === null ? (
        <NotRecorded />
      ) : (
        <span className="flex items-baseline gap-1">
          <span className="text-2xs text-fg-subtle">{service.currency}</span>
          <span className="tabular">{formatDecimalString(headline)}</span>
        </span>
      )}
      <VatNote vat={vat} currency={service.currency} />
      {/*
       * The invoice is shown only when there is one AND we hold its local id:
       * a number with no id cannot be linked (the document was raised inside
       * Xero and only its number came back), and a bare number in a money cell
       * reads as part of the figure.
       *
       * ⚠️ **A LINK THAT OPENS THE EDIT FORM IN PLACE — owner's instruction.**
       * The `href` is still `/invoices/{id}`, so a modified click still opens
       * the detail page in a tab; only the plain click is intercepted. The form
       * is mounted ONCE for the whole page by `InvoiceQuickEdit` — one per
       * surface, never one per cell, and this cross-tab can show dozens of
       * numbers at a time.
       *
       * ⚠️ **THE ID-LESS BRANCH BELOW IS UNCHANGED.** `InvoiceNumberLink` needs
       * the id for its `href` and for the read, so a number with no id stays
       * plain text — the honest render of a document this app cannot open.
       */}
      {label !== undefined && label !== '' && service.invoiceId !== null && (
        <InvoiceNumberLink
          invoiceId={service.invoiceId}
          invoiceNumber={label}
          title={label}
          className="block max-w-full truncate text-2xs text-fg underline-offset-2 decoration-hairline-strong transition-colors duration-150 ease-out hover:underline motion-reduce:transition-none"
        />
      )}
      {label !== undefined && label !== '' && service.invoiceId === null && (
        <span title={label} className="block max-w-full truncate text-2xs text-fg-subtle">
          {label}
        </span>
      )}
    </span>
  );
}

/* -------------------------------------------------------------------------
 * The header
 * --------------------------------------------------------------------- */

/** The affordance on the ONE editable column, so it is answerable without hovering a row. */
function EditableGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-3 w-3 shrink-0 text-fg-subtle"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

/**
 * A two-tier header: one row of cycle names, one row of the four columns each.
 *
 * ⚠️ **The company header spans BOTH rows** (`rowSpan={2}`), which is what keeps
 * the frozen column one cell rather than two stacked ones with a hairline
 * between them. **The separator columns span both rows for the same reason** —
 * the dark bar has to run unbroken from the top of the table down, and two
 * stacked separator cells would show the header's own rule across it.
 *
 * ⚠️ **THE ROW RULE IS SUPPRESSED HERE TOO.** The line under the cycle names is
 * drawn by the header cells' own bottom borders, not by `TR` — see `CELL_RULE`.
 * Left on, `TR`'s rule would be the one thing still drawing across a column
 * whose whole job is to be uninterrupted.
 *
 * 🔴 **THE `Notes` HEADING CARRIES THE EDIT AFFORDANCE, and that is the whole
 * read-only story on this page.** Every other cell is derived and renders as
 * plain text with no control — which is correct, and is also indistinguishable
 * from a control somebody forgot to add. `InlineField`'s pencil is visible at
 * rest on each notes cell (its own header records a feature reported as MISSING
 * because the affordance only appeared on hover), and this repeats the claim on
 * the column itself so the question is answered before anybody hovers anything.
 */
/**
 * The first header row's rendered height, in px — the second row's sticky `top`.
 *
 * 🔴 **MEASURED, NOT ASSUMED.** A hard-coded offset is right until the type
 * scale, the padding or a wrapped cycle name moves the first row by a pixel,
 * and then the second row either overlaps it or leaves a slit the body shows
 * through — with nothing failing. A `ResizeObserver` on the first `<tr>` keeps
 * the two in step whatever moves. It fires once on `observe`, so the state is
 * set from the observer and never synchronously inside the effect.
 *
 * ⚠️ **The fallback (36px) is only the frame before the first measurement**:
 * `py-2` plus `text-xs`'s 18px line-height is 34px, rounded up by `h-9`.
 */
const FIRST_HEADER_ROW_FALLBACK_PX = 36;

function useRowHeight(): [number, (element: HTMLElement | null) => void] {
  const [row, setRow] = useState<HTMLTableRowElement | null>(null);
  const [height, setHeight] = useState(FIRST_HEADER_ROW_FALLBACK_PX);
  const anchor = useCallback((element: HTMLElement | null) => {
    setRow(element?.closest('tr') ?? null);
  }, []);
  useEffect(() => {
    if (row === null || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      const next = row.getBoundingClientRect().height;
      if (next > 0) setHeight((current) => (current === next ? current : next));
    });
    observer.observe(row);
    return () => observer.disconnect();
  }, [row]);
  return [height, anchor];
}

function RevenueTableHead({ cycles }: { cycles: readonly string[] }) {
  const [firstRowHeight, firstRowAnchor] = useRowHeight();
  const secondRowTop = { top: `${firstRowHeight}px` };
  return (
    <THead>
      <TR style={NO_ROW_RULE}>
        <TH rowSpan={2} className={cx(COMPANY_W, FROZEN_TH)}>
          Company Name
        </TH>
        {cycles.map((cycle, index) => (
          <Fragment key={cycle}>
            {index > 0 && <CycleSeparator rowSpan={2} header />}
            <TH
              scope="colgroup"
              colSpan={4}
              className={cx('h-9 top-0 text-center', CELL_RULE, STICKY_HEADER_CELL)}
            >
              {/*
               * The measuring anchor: the span finds its own `<tr>`. Only the
               * first cycle carries it — one observer is enough for one row.
               */}
              <span ref={index === 0 ? firstRowAnchor : undefined}>{cycle}</span>
            </TH>
          </Fragment>
        ))}
      </TR>
      <TR style={NO_ROW_RULE}>
        {cycles.map((cycle) => (
          <Fragment key={cycle}>
            <TH style={secondRowTop} className={cx(SERVICE_W, CELL_RULE, STICKY_HEADER_CELL)}>
              Service Name
            </TH>
            <TH
              numeric
              style={secondRowTop}
              className={cx(AMOUNT_W, CELL_RULE, STICKY_HEADER_CELL)}
            >
              Amount
            </TH>
            <TH
              numeric
              style={secondRowTop}
              className={cx(TOTAL_W, CELL_RULE, STICKY_HEADER_CELL)}
            >
              Total
            </TH>
            <TH style={secondRowTop} className={cx(NOTES_W, CELL_RULE, STICKY_HEADER_CELL)}>
              <span className="inline-flex items-center gap-1">
                Notes
                <EditableGlyph />
                <span className="sr-only">(the only editable column)</span>
              </span>
            </TH>
          </Fragment>
        ))}
      </TR>
    </THead>
  );
}

/* -------------------------------------------------------------------------
 * A company block
 * --------------------------------------------------------------------- */

/**
 * One company, as one or more `<tr>`s.
 *
 * 🔴 **SEVERAL SERVICES IN ONE CYCLE STACK ON CONTINUATION ROWS, AND THE COMPANY
 * CELL IS MERGED ACROSS THEM WITH A REAL `rowSpan`.** That is the workbook's
 * shape — the client name is one tall, vertically centred box beside its service
 * rows, not a name followed by blanks — and it is what makes the table and the
 * sheet read alike. The block is as tall as the busiest cycle; a cycle with
 * fewer services simply runs out of lines.
 *
 * ⚠️ *SUPERSEDED: "continuation rows whose company cell is BLANK."* They no
 * longer have one at all. A `rowSpan` cell is not a cell plus some empty ones:
 * the continuation rows must OMIT the company, Total and Notes `<td>`s
 * entirely, because the spanned cell already occupies those grid slots and an
 * empty `<td>` beside it would push every later column one place right — on the
 * continuation rows only, which is why it draws a staircase rather than an
 * error.
 *
 * ⚠️ **THE `sr-only` "…continued" TEXT IS GONE, AND ITS ABSENCE IS THE FIX
 * RATHER THAN A REGRESSION.** It existed because a blank cell told a screen
 * reader nothing, leaving a row of figures belonging to nobody. A `rowSpan`
 * cell is genuinely associated with every row it spans, so assistive technology
 * announces the company on each of them from the markup itself — and repeating
 * it in hidden text would now say the name twice.
 *
 * ⚠️ **`Total` AND `Notes` ARE MERGED THE SAME WAY**, because they belong to the
 * (company, cycle) pair and not to a service. They were already rendered on the
 * first line only; the `rowSpan` is what makes them look deliberate instead of
 * looking like a value that failed to repeat. Repeating them down the block
 * would show one figure several times and mount several editors over one stored
 * note, of which the last to save would win silently.
 *
 * 🔴 **THE ROW RULE IS SUPPRESSED ON EVERY ROW OF THE BLOCK, NOT JUST THE
 * INTERNAL ONES.** `TR`'s `border-b` crosses the full width of the row, so on a
 * block whose company cell spans four lines it would paint three rules straight
 * THROUGH that cell. The whole grid is drawn from cell borders instead — see
 * `CELL_RULE` — and the block's own bottom edge comes from its last line's cell
 * borders plus the frozen cell's inset shadow. An inline `style` and not a
 * class: same-property Tailwind utilities do not resolve by the order they
 * appear in a `class` attribute, so a `border-b-0` through `cx` wins or loses
 * depending on the order Tailwind happened to emit them in.
 *
 * ⚠️ **The rows are NOT `interactive`.** A row-level hover would light one
 * service line out of a block, which reads as the block having come apart; and
 * nothing here navigates from a row — the company name and the invoice numbers
 * are links, which is where a click belongs.
 */
function CompanyBlock({
  row,
  cycles,
  tint,
  writer,
  onNotesSaved,
}: {
  row: RevenueTrackerRow;
  cycles: readonly string[];
  tint: BlockTint;
  writer: RevenueNotesWriter;
  onNotesSaved: (rowKey: string, cycleKey: string, stored: string | null) => void;
}) {
  /*
   * `Math.max(1, ...[])` is `1`, so a company with no services anywhere — or a
   * page with every cycle column filtered out — still renders one line rather
   * than none. A block of zero rows is a company that silently vanishes.
   */
  const lines = Math.max(1, ...cycles.map((cycle) => cellOf(row, cycle).services.length));

  return (
    <>
      {Array.from({ length: lines }, (_, line) => {
        /*
         * ⚠️ Every merged cell is rendered on the FIRST line and omitted on all
         * the rest, and `lines` is the block's own height — so one flag and one
         * span drive the company cell and every cycle's Total and Notes at once.
         * They cannot drift into spanning different distances, which would not
         * throw: it would draw a block with a ragged bottom edge.
         */
        const isFirstLine = line === 0;
        return (
          <TR
            /*
             * ⚠️ The BLOCK's identity is `row.key` and it is applied by the
             * caller. Within a block the lines have no identity of their own —
             * they are positions in a service list — so the index is the honest
             * key here, and prefixing it with `row.key` keeps it unique across
             * the two companies that share the display name `Think Test
             * Company`.
             */
            key={`${row.key}:${line}`}
            style={NO_ROW_RULE}
          >
            {isFirstLine && (
              <TD
                rowSpan={lines}
                className={cx(COMPANY_MAX_W, frozenCompanyCell(tint.frozen))}
              >
                <TextLink
                  to={`/companies/${row.company.id}`}
                  title={row.company.name}
                  className="block truncate text-sm"
                >
                  {row.company.name}
                </TextLink>
              </TD>
            )}

            {cycles.map((cycle, index) => {
              const cell = cellOf(row, cycle);
              const service = cell.services[line];
              return (
                <Fragment key={cycle}>
                  {/*
                   * The separator belongs to the group it opens, so it is
                   * rendered on every line rather than merged: with no border of
                   * its own and nothing drawing across it, the stacked cells
                   * read as one unbroken bar anyway, and a `rowSpan` here would
                   * have to be kept in step with the block's height for no gain.
                   */}
                  {index > 0 && <CycleSeparator />}
                  <TD className={cx(SERVICE_MAX_W, CELL_RULE, tint.cells)}>
                    {service ? <ServiceNameCell service={service} /> : null}
                  </TD>
                  <TD numeric className={cx('whitespace-nowrap', CELL_RULE, tint.cells)}>
                    {service ? <AmountCell service={service} /> : null}
                  </TD>
                  {isFirstLine && (
                    <TD
                      numeric
                      rowSpan={lines}
                      className={cx('whitespace-nowrap', CELL_RULE, tint.cells)}
                    >
                      <TotalCell cell={cell} />
                    </TD>
                  )}
                  {isFirstLine && (
                    <TD rowSpan={lines} className={cx(NOTES_MAX_W, CELL_RULE, tint.cells)}>
                      <RevenueNotesField
                        companyId={row.company.id}
                        cycleKey={cycle}
                        cycleLabel={cycle}
                        notes={cell.notes}
                        writer={writer}
                        onSaved={(stored) => onNotesSaved(row.key, cycle, stored)}
                      />
                    </TD>
                  )}
                </Fragment>
              );
            })}
          </TR>
        );
      })}
    </>
  );
}

/* -------------------------------------------------------------------------
 * The TOTAL rows
 * --------------------------------------------------------------------- */

/**
 * What a totals row shows for a cycle the server said nothing about.
 *
 * ⚠️ **A GUARD AGAINST A NARROWER ANSWER, NOT A ZERO.** It is the same shape as
 * `EMPTY_CELL` and for the same reason: a cycle can legitimately have a column
 * without a band having a figure for it, and the honest render of that is a
 * withheld total, never `0.00`.
 */
const EMPTY_TOTAL: RevenueTrackerTotalCell = { total: null, totalCurrency: null };

/** How a withheld total is worded on each of the two totals rows. */
const BAND_WORDING = { subject: 'This band', scope: 'this band in this cycle' } as const;
const GRAND_WORDING = { subject: 'This column', scope: 'this column' } as const;

/**
 * One closing row — a band's `TOTAL`, or the table's grand total.
 *
 * 🔴 **ONE COMPONENT FOR BOTH, SO THEY CANNOT DRIFT APART STRUCTURALLY.** They
 * are the same row at two weights: the same column arrangement, the same
 * withheld-total treatment, the same `<th scope="row">`. Two components would
 * agree the day they were written and diverge on the first correction — and what
 * would diverge is whether a figure closing a table is allowed to print `0.00`.
 *
 * 🔴 **THE LABEL IS A `<th scope="row">`, LIKE THE BAND HEADING ABOVE IT.** It
 * is a row header, not a cell that happens to hold a word: a screen reader
 * moving across this row must announce *"Total"* against each figure rather than
 * meeting a stray string and then a run of numbers belonging to nobody. It sits
 * in the frozen company column, which is also what keeps the word on screen when
 * the reader has scrolled to a later cycle.
 *
 * ⚠️ **THE FIGURE GOES UNDER `Total`, AND `Service Name` / `Amount` / `Notes`
 * ARE RENDERED EMPTY RATHER THAN SPANNED.** The column a number sits in is a
 * claim about what it totals, so the band's total belongs under the column whose
 * values it sums and nowhere else. Empty cells keep the grid drawn across the
 * row — a `colSpan` here would open a gap in a table whose whole rule structure
 * comes from cell borders.
 *
 * ⚠️ **THE ROW READS AS A TOTAL FROM ITS TEXT.** The surface step is grouping,
 * not meaning; strip the colour and *"Total · Europe"* against a bold figure is
 * still unambiguous. See the chrome constants above.
 */
function TotalsRow({
  variant,
  label,
  qualifier,
  cycles,
  totals,
  rows,
}: {
  variant: 'band' | 'grand';
  label: string;
  /** The band's own name, or what a filter has made the grand total mean. */
  qualifier?: string;
  cycles: readonly string[];
  totals: Record<string, RevenueTrackerTotalCell>;
  /** The rows this row closes — evidence for a withheld total, never summed. */
  rows: readonly RevenueTrackerRow[];
}) {
  const isGrand = variant === 'grand';
  const cellClass = isGrand ? GRAND_TOTAL_CELL : BAND_TOTAL_CELL;
  const frozenClass = isGrand ? GRAND_TOTAL_FROZEN : BAND_TOTAL_FROZEN;
  const wording = isGrand ? GRAND_WORDING : BAND_WORDING;

  return (
    <TR style={NO_ROW_RULE}>
      {/*
       * ⚠️ **THE WEIGHT AND COLOUR ARE ON AN INNER `<span>`, NOT ON THE `<th>`.**
       * `TH scope="row"` bakes in `font-medium text-fg-secondary`, and both are
       * properties an appended utility would have to WIN — which it does or does
       * not depending on the order Tailwind emitted the two, exactly the trap
       * `NO_ROW_RULE` documents. A child element inherits nothing it declares
       * itself, so this is decided rather than raced.
       */}
      <TH scope="row" className={cx(COMPANY_MAX_W, frozenClass)}>
        <span className="flex min-w-0 items-baseline gap-2">
          <span className="shrink-0 font-semibold text-fg">{label}</span>
          {qualifier !== undefined && (
            <span title={qualifier} className="min-w-0 truncate text-2xs font-normal text-fg-muted">
              {qualifier}
            </span>
          )}
        </span>
      </TH>

      {cycles.map((cycle, index) => (
        <Fragment key={cycle}>
          {index > 0 && <CycleSeparator />}
          <TD className={cx(CELL_RULE, cellClass)} />
          <TD numeric className={cx(CELL_RULE, cellClass)} />
          <TD numeric className={cx('whitespace-nowrap', CELL_RULE, cellClass)}>
            <AggregateTotalCell
              cell={totals[cycle] ?? EMPTY_TOTAL}
              rows={rows}
              cycle={cycle}
              wording={wording}
            />
          </TD>
          <TD className={cx(CELL_RULE, cellClass)} />
        </Fragment>
      ))}
    </TR>
  );
}

/**
 * The same closing figures below `lg`, where there is no table to close.
 *
 * 🔴 **IT IS HERE BECAUSE THE TWO VIEWS MUST NOT DISAGREE ABOUT WHAT DATA
 * EXISTS.** `CompanyCard`'s own header records the rule this follows: a feature
 * reachable only above a breakpoint is indistinguishable from one that was
 * removed — and a band total is the single figure the workbook's readers look
 * for first, so leaving it out below `lg` would be leaving out the answer while
 * keeping the working.
 *
 * ⚠️ **A `<dl>`, NOT A ROW.** There is no grid here to close, so the shape that
 * carries the meaning is cycle-and-its-figure as a pair — which is what a
 * description list is. The em dash, the withheld-total sentence and the currency
 * beside every figure are the table's, unchanged, because they are decisions
 * about honesty rather than about layout.
 */
function TotalsCard({
  variant,
  label,
  qualifier,
  cycles,
  totals,
  rows,
}: {
  variant: 'band' | 'grand';
  label: string;
  qualifier?: string;
  cycles: readonly string[];
  totals: Record<string, RevenueTrackerTotalCell>;
  rows: readonly RevenueTrackerRow[];
}) {
  const isGrand = variant === 'grand';
  const wording = isGrand ? GRAND_WORDING : BAND_WORDING;

  return (
    <div
      className={cx(
        'border-b border-border px-4 py-3 shadow-[inset_0_1px_0_0_var(--hairline-strong)]',
        isGrand ? 'bg-surface-active' : 'bg-surface-sunken',
      )}
    >
      <p className="m-0 flex flex-wrap items-baseline gap-2">
        <span className="text-xs font-semibold text-fg">{label}</span>
        {qualifier !== undefined && (
          <span className="min-w-0 text-2xs text-fg-muted [overflow-wrap:anywhere]">
            {qualifier}
          </span>
        )}
      </p>
      <dl className="mt-2 mb-0 grid gap-1">
        {cycles.map((cycle) => (
          <div key={cycle} className="flex items-baseline justify-between gap-2">
            <dt className="min-w-0 truncate text-xs font-medium text-fg-muted">
              {cycle}
            </dt>
            <dd className="m-0 shrink-0 text-xs">
              <AggregateTotalCell
                cell={totals[cycle] ?? EMPTY_TOTAL}
                rows={rows}
                cycle={cycle}
                wording={wording}
              />
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Below `lg`
 * --------------------------------------------------------------------- */

/**
 * The same data as a stacked list.
 *
 * ⚠️ **A cross-tab cannot be a table at 320px** — four cycles is nineteen
 * columns — so below `lg` a company becomes a card and each cycle a block
 * inside it. It is NOT a reduced set: every cycle, every service, every total
 * and the same notes editor are here, because a feature reachable only above a
 * breakpoint is indistinguishable from one that was removed.
 */
function CompanyCard({
  row,
  cycles,
  writer,
  onNotesSaved,
}: {
  row: RevenueTrackerRow;
  cycles: readonly string[];
  writer: RevenueNotesWriter;
  onNotesSaved: (rowKey: string, cycleKey: string, stored: string | null) => void;
}) {
  return (
    <article className="border-b border-border px-4 py-3 last:border-b-0">
      <TextLink to={`/companies/${row.company.id}`} className="block truncate text-sm font-medium">
        {row.company.name}
      </TextLink>

      {cycles.map((cycle) => {
        const cell = cellOf(row, cycle);
        return (
          <div key={cycle} className="mt-3 rounded-md border border-border p-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-xs font-medium text-fg-muted">
                {cycle}
              </span>
              <span className="text-xs">
                <TotalCell cell={cell} />
              </span>
            </div>

            {cell.services.length === 0 ? (
              <p className="mt-2 mb-0 text-xs text-fg-subtle">No services in this cycle.</p>
            ) : (
              <ul className="mt-2 mb-0 grid list-none gap-1 p-0">
                {cell.services.map((service, index) => {
                  const figure = taxInclusiveFigure(
                    service.amount,
                    service.amountInclTax,
                    service.tax,
                  );
                  return (
                    <li
                      // Services carry no id of their own; position is the only
                      // identity available and the list is never reordered here.
                      key={`${cycle}:${index}`}
                      className="flex items-baseline justify-between gap-2"
                    >
                      <span className="min-w-0 text-xs text-fg-secondary [overflow-wrap:anywhere]">
                        {service.serviceName.trim() || '—'}
                      </span>
                      <span className="flex shrink-0 flex-col items-end gap-0.5 text-xs whitespace-nowrap tabular">
                        {figure.headline === null ? (
                          <NotRecorded />
                        ) : (
                          <span>
                            <span className="mr-1 text-2xs text-fg-subtle">{service.currency}</span>
                            {formatDecimalString(figure.headline)}
                          </span>
                        )}
                        <VatNote vat={figure.vat} currency={service.currency} />
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="mt-3 border-t border-border pt-2">
              <p className="m-0 mb-1 flex items-center gap-1 text-xs font-medium text-fg-muted">
                Notes
                <EditableGlyph />
              </p>
              <RevenueNotesField
                companyId={row.company.id}
                cycleKey={cycle}
                cycleLabel={cycle}
                notes={cell.notes}
                writer={writer}
                onSaved={(stored) => onNotesSaved(row.key, cycle, stored)}
              />
            </div>
          </div>
        );
      })}
    </article>
  );
}

/* -------------------------------------------------------------------------
 * Skeletons and the view toggle
 * --------------------------------------------------------------------- */

const SKELETON_ROWS = 6;

/*
 * ⚠️ **THE SKELETON IS ONE LINE PER COMPANY AND CARRIES NO BAND TINT, AND BOTH
 * are deliberate.** How tall a block is and which band it falls in are the two
 * things the response has not said yet, so a skeleton that guessed either would
 * be showing a shape the data is about to contradict. It still draws the
 * SEPARATOR columns and the cell grid, because those are structural and known
 * from the cycle count alone — a skeleton whose column arrangement does not
 * match the table it precedes reads as the page jumping when the data lands.
 *
 * The frozen cell is `bg-surface` here for the same reason it is on a plain
 * block: it is sticky, so it must be opaque whatever else is true.
 */
function TableSkeleton({ cycleCount }: { cycleCount: number }) {
  return (
    <TBody>
      {Array.from({ length: SKELETON_ROWS }, (_, rowIndex) => (
        <TR key={rowIndex} style={NO_ROW_RULE}>
          <TD className={cx(COMPANY_MAX_W, frozenCompanyCell(PLAIN_BLOCK.frozen))}>
            <Skeleton className="h-3 w-40" />
          </TD>
          {Array.from({ length: cycleCount }, (_, cycleIndex) => (
            <Fragment key={cycleIndex}>
              {cycleIndex > 0 && <CycleSeparator />}
              <TD className={CELL_RULE}>
                <Skeleton className="h-3 w-32" />
              </TD>
              <TD numeric className={CELL_RULE}>
                <Skeleton className="ml-auto h-3 w-16" />
              </TD>
              <TD numeric className={CELL_RULE}>
                <Skeleton className="ml-auto h-3 w-16" />
              </TD>
              <TD className={CELL_RULE}>
                <Skeleton className="h-3 w-28" />
              </TD>
            </Fragment>
          ))}
        </TR>
      ))}
    </TBody>
  );
}

/**
 * 🔴 **THE SHEET IS THE DEFAULT AND `?view=table` IS THE OPT-IN** — owner's
 * instruction, 2026-09-02, matching `/client-deliverables` and the Accounts
 * dashboard. *SUPERSEDED: "**THE TABLE IS THE DEFAULT AND MUST STAY THE
 * DEFAULT.** It is the view that filters, and — the half that is easy to forget
 * — the only one that works for a reader with no Google access at all. The
 * mirror is a convenience over the same data; opening onto it would put a
 * document somebody may not be able to see in front of them instead of the page
 * they navigated to."*
 *
 * ⚠️ **THE SUPERSEDED REASONING WAS NOT WRONG, AND ITS SECOND HALF IS STILL
 * LIVE.** A reader with no Google access still cannot see the frame, and a
 * cross-origin frame that fails to paint **reports nothing** — so for that person
 * the default view is now a blank rectangle. What answers it is not this
 * resolution but the "Not showing above?" line under the frame, which is
 * unconditional precisely because this side cannot observe the failure. **Do not
 * gate that line on anything.**
 *
 * The half that genuinely expired is "the table is the view that filters": the
 * mirrored tab carries Google's own filter view, and the owner has been reading
 * the sheet rather than the table.
 */
type TrackerView = 'table' | 'sheet';

const VIEW_OPTIONS: readonly { value: TrackerView; label: string }[] = [
  { value: 'table', label: 'Table' },
  { value: 'sheet', label: 'Sheet' },
];

/**
 * The Table/Sheet switch, matching the one on `/client-deliverables`,
 * `/invoice-tracker`, `/companies`, `/deals`, `/quotes` and `/invoices`.
 *
 * ⚠️ **`SegmentedControl` exists in `src/components/ui` and is deliberately NOT
 * used.** It is built for a TABLE CELL — `w-full` with `flex-1` segments and a
 * reserved tick box on every option — so in `PageHeader`'s actions slot it
 * stretches the choice across the header. This button group is what every other
 * page-level view switch in this app already is.
 *
 * `role="group"` with `aria-pressed`, NOT `role="tab"`: these switch what the
 * panel below IS, rather than labelling panels that share one tabstop.
 *
 * ⚠️ **Both options render at every width, always.** This codebase has already
 * had a working feature reported as *deleted* when it had merely become
 * unreachable behind a view toggle.
 */
function ViewToggle({ view, onChange }: { view: TrackerView; onChange: (next: TrackerView) => void }) {
  return (
    <SharedViewToggle
      label="Revenue tracker view"
      value={view}
      options={VIEW_OPTIONS}
      onChange={onChange}
    />
  );
}

/* -------------------------------------------------------------------------
 * Page
 * --------------------------------------------------------------------- */

/**
 * The value a filter carries when it is not filtering — and it is `''` because
 * that is what `setParam` deletes the key for, so "unfiltered" is spelled by the
 * key's ABSENCE and there is one canonical URL per state.
 *
 * ⚠️ *SUPERSEDED 2026-09-13: "Every filter this page applies, and the view. **All
 * client-side but the page.**"* **The company search goes to the server now.**
 * `region` and `cycle` are still browser-side; the reason each is still there is
 * written at `visibleRows` and `visibleCycles` respectively, and neither is
 * "nobody got round to it".
 */
const ALL = '';

/**
 * Every value `?billingRegion=` accepts.
 *
 * 🔴 **BUILT SO A MISSING MEMBER IS A BUILD FAILURE.** `satisfies
 * Record<BillingRegionFilter, true>` refuses an incomplete object *and* an
 * unknown key, so a fifth band added to the union cannot quietly stop being
 * sendable here — which would be the worst shape of all: the master offers the
 * new band, the picker lists it, and the guard below silently reads it as "all
 * bands" and shows the whole tracker under its name.
 *
 * ⚠️ **A plain array would typecheck happily while being one short**, which is
 * exactly the class of defect this repo records against lists written out twice.
 */
const BILLING_REGION_FILTERS = Object.keys({
  united_kingdom: true,
  europe: true,
  rest_of_world: true,
  north_america: true,
  none: true,
} satisfies Record<BillingRegionFilter, true>) as readonly BillingRegionFilter[];

export function RevenueTrackerPage() {
  const { ability } = useAuth();

  /*
   * The permission check. A hidden sidebar row is an optimisation and never a
   * guarantee — the URL is typeable, the command palette offers it, and a
   * permission set can change under an access token that lasts days — so the
   * page answers for itself.
   *
   * ⚠️ This DEGRADES OPEN — see `NAV_SURFACE_MODULE` in `@/lib/permissions`.
   * Only an explicit `none` scope denies; an unseeded catalogue, an older API or
   * a failed `/permissions/me` all leave the page visible.
   *
   * ⚠️ **Hiding a page is not refusing a request.** `GET /revenue-tracker`
   * answers the same to everybody, so nothing here may be worded as though the
   * data were protected. This is a view control.
   *
   * ⚠️ Computed here rather than as an early return, because the return has to
   * sit below every hook.
   */
  const canReadSurface = ability.canRead(NAV_SURFACE_MODULE.trackerRevenue);

  const [searchParams, setSearchParams] = useSearchParams();
  const [result, setResult] = useState<RevenueTrackerList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  /**
   * The billing-region master, so the band picker does not read the response it
   * is filtering.
   *
   * 🔴 **ONE UNPAGINATED READ, ONCE PER MOUNT, AND IT IS WHAT LETS THE BAND
   * FILTER GO TO THE WIRE.** See `regionOptions`: a picker built from `rows`
   * collapses to the band already chosen the moment the server does the
   * narrowing. This is the *read the master, never the response you are
   * filtering* rule, which this page already states for the cycle.
   *
   * ⚠️ **A FAILURE IS SWALLOWED ON PURPOSE AND IS NOT AN ERROR STATE.** The
   * table does not depend on it — `regionOptions` falls back to deriving from
   * the rows — so a red banner here would report a broken page over a report
   * that is entirely fine. It is `[]` until it answers, which is the same thing
   * a server without the endpoint says.
   */
  const [regionMaster, setRegionMaster] = useState<readonly BillingRegionDefault[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listBillingRegionDefaults(controller.signal)
      .then((envelope) => {
        if (controller.signal.aborted) return;
        setRegionMaster(envelope.data);
      })
      .catch(() => {
        /* Deliberately silent — see the state above. */
      });
    return () => controller.abort();
  }, []);

  const searchParamsKey = searchParams.toString();
  const params = useMemo(() => new URLSearchParams(searchParamsKey), [searchParamsKey]);
  const search = params.get('q') ?? '';
  /**
   * The band filter, **resolved against the five the server accepts**.
   *
   * 🔴 **AN UNKNOWN VALUE READS AS "ALL BANDS" RATHER THAN GOING TO THE WIRE,
   * AND THAT IS NOT DEFENSIVENESS — IT IS THE DIFFERENCE BETWEEN A WIDE REPORT
   * AND NO REPORT.** `?billingRegion=` is a `.strict()` enum, so a stale or
   * mangled value would be a **422 that takes the whole table out**, not one
   * empty option. These land in a URL that gets typed, pasted and truncated by
   * chat clients.
   *
   * ⚠️ **IT ALSO KEEPS THE CONTROL HONEST.** The picker's options are the five
   * members and nothing else now, so a value outside them has no row to select
   * and a `Combobox` would fall back to its PLACEHOLDER — "All billing regions"
   * printed over a table narrowed to something else. Resolving here means the
   * label and the figures cannot disagree: it really is all bands.
   *
   * ⚠️ **The same rule `cycleKey` follows one line down, and the OPPOSITE of
   * what the Revenue by Company tab does with its own band key** — that one
   * deliberately keeps an unresolved value and injects it back as an option,
   * because there the filter runs in the browser and asking for a band nobody
   * traded in is a real question with a real answer. Here it is a 422.
   */
  /*
   * Several bands and several cycles (owner 2026-10-28): `?region=` and
   * `?cycle=` hold a comma-separated list; a single value (every old bookmark)
   * is a list of one. Each region member is resolved against the five on its
   * own, so one bad entry drops out rather than 422ing the whole read.
   */
  const rawRegion = params.get('region') ?? ALL;
  const regionKeys = useMemo(
    () =>
      [
        ...new Set(
          rawRegion
            .split(',')
            .map((part) => part.trim())
            .filter((part): part is BillingRegionFilter =>
              (BILLING_REGION_FILTERS as readonly string[]).includes(part),
            ),
        ),
      ],
    [rawRegion],
  );
  const regionKey = regionKeys.join(',');
  const rawCycle = params.get('cycle') ?? ALL;
  const cycleKeys = useMemo(
    () => [...new Set(rawCycle.split(',').map((part) => part.trim()).filter((part) => part !== ''))],
    [rawCycle],
  );
  const cycleKey = cycleKeys.join(',');
  /*
   * ⚠️ **TRIMMED ONCE, HERE, BECAUSE IT IS NOW A REQUEST KEY.** The server
   * trims too (`z.string().trim()`) and `companyFilter` then tests `!== ''`, so
   * a box holding only spaces narrows nothing at either end — but trimming
   * before the dependency array is what stops a trailing space counting as a
   * changed query and firing a second identical read.
   */
  const searchTerm = search.trim();

  const setParam = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(searchParamsKey);
      // A default is spelled by the key's ABSENCE — one canonical URL per view.
      if (value === '') next.delete(key);
      else next.set(key, value);
      /*
       * `replace: true`: changing a filter is not a navigation, and a history
       * entry per keystroke would make Back mean "undo one filter".
       */
      setSearchParams(next, { replace: true });
    },
    [searchParamsKey, setSearchParams],
  );

  /*
   * 🔴 **A SERVER-SCOPED FILTER RESETS THE PAGE; A PAGE-SCOPED ONE MUST NOT.**
   * `ContactsPage` states the rule and `/users` shipped without it: searching
   * from page 3 asks the server for page 3 of a SHORTER result, which comes back
   * empty and reads as *"nothing matches"* while every match sits on page 1.
   *
   * ⚠️ **AND THE CONVERSE IS A REAL COST, NOT SYMMETRY TO BE TIDIED IN.** The
   * region and cycle filters narrow the rows already in hand, so resetting the
   * page for them would throw away the very rows the reader was filtering. Only
   * `q` goes through here, and `region` / `cycle` deliberately do not — the day
   * one of them moves to the wire, it moves to this function in the same change.
   *
   * Folded into the event rather than an effect watching `searchTerm`, so no
   * request is ever fired for a page that no longer exists.
   */
  const setSearch = useCallback(
    (value: string) => {
      setParam('q', value);
      setPage(1);
    },
    [setParam],
  );

  /**
   * ✅ **THE BAND MOVED TO THE WIRE ON 2026-09-14, AND SO IT MOVED TO THIS
   * FUNCTION IN THE SAME CHANGE** — which is what the note above said would
   * happen, written before it did.
   *
   * ⚠️ **THE RESET IS THE WHOLE REASON THIS EXISTS.** Narrowing to a band from
   * page 3 asks the server for page 3 of a much shorter result, which comes back
   * empty and reads as *"no company is in this band"* while every one of them
   * sits on page 1. `/users` shipped exactly that defect.
   */
  const setRegion = useCallback(
    (values: readonly string[]) => {
      setParam('region', values.join(','));
      setPage(1);
    },
    [setParam],
  );

  /* ── the mirror ──────────────────────────────────────────────────────── */

  const sheet = useRevenueTrackerSheet();
  /*
   * ⚠️ **THE TAB IS GATED ON THE PREVIEW URL, NOT ON `link` AND NOT ON
   * `configured`.** Three states collapse to "there is nothing to embed": no
   * mirror configured, the answer not known yet, and — the one this exists for
   * — a server with no `/revenue-tracker/sheet` route at all, which is every
   * server at the time this page was written. All three must land on the table.
   */
  const sheetUrl = sheet.loading ? null : sheet.previewLink;
  /*
   * ⚠️ **`?? sheetUrl` IS NOT TIDINESS.** The EDIT address is the one a current
   * server always sends and the preview is the one an older server omits, so
   * this pair reads backwards from `sheetUrl` above on purpose. Never derive one
   * from the other by string surgery — the shape of a Google address is the
   * API's fact, and a bad swap paints an empty frame that looks exactly like a
   * permissions problem.
   */
  const sheetEmbedUrl = sheet.loading ? null : (sheet.link ?? sheetUrl);
  const canShowSheet = sheetUrl !== null;
  /*
   * ⚠️ `canShowSheet` is applied on the READ, so a shared `?view=sheet` link on
   * a box with no mirror lands on the table rather than on a blank frame beside
   * a toggle that is not rendered. The param is left in the URL untouched:
   * rewriting somebody's link on arrival is a second writer on the query
   * string, and the same link resolves correctly the day the mirror exists.
   */
  /*
   * ⚠️ **A URL THAT NAMES A VIEW STILL WINS, IN BOTH DIRECTIONS.** `?view=table`
   * renders the table and `?view=sheet` renders the sheet; only an ABSENT or
   * unrecognised key falls to the default. So every `?view=sheet` link this page
   * has minted since it shipped still resolves to the view it names, and a shared
   * address keeps meaning one thing.
   *
   * ⚠️ **NOTHING IS WRITTEN INTO THE URL ON ARRIVAL, and that is what keeps the
   * default clearable.** `table` is expressible as a value, so choosing it writes
   * `?view=table` and that survives a reload, the Back button and a shared link.
   * Writing the resolved view on arrival would buy nothing and would put a second
   * writer on the query string.
   */
  const wantsTable = params.get('view') === 'table';
  const view: TrackerView = !wantsTable && canShowSheet ? 'sheet' : 'table';
  /*
   * ⚠️ **THE UNDECIDED CASE IS ITS OWN STATE, and it became necessary WITH the
   * default rather than being tidiness.** The mirror's address is not known on
   * the first paint — `useRevenueSheetLink` reads it over the network — so
   * `canShowSheet` is false for that window and the resolution above answers
   * `table`. That would render a whole table, fire its list request, and swap it
   * for the frame a moment later: the default view arriving as a visible
   * replacement of another one, which reads as the page changing its mind.
   *
   * ⚠️ **`view` cannot answer this on its own** — it computes to `table` during
   * exactly the window the placeholder covers — so it is tested against the raw
   * param, and only when the reader has NOT asked for the table, because that
   * request needs no network answer and must never wait on one.
   */
  const resolvingSheet = !wantsTable && sheet.loading;
  const sheetOpenUrl = sheet.loading ? null : sheet.link;

  /* ── the read ────────────────────────────────────────────────────────── */

  /*
   * ⚠️ *SUPERSEDED 2026-09-13, the REASON and not the behaviour: "**`page` IS
   * SENT ONLY ONCE THE SERVER HAS PROVED IT PAGINATES.** The contract for this
   * endpoint documents no query parameters … So the first request carries
   * nothing at all."* **The endpoint publishes a full `.strict()` query schema**
   * — measured 2026-09-11, recorded on `RevenueTrackerListParams` — so "the
   * contract documents no parameters" is no longer why anything here is omitted.
   *
   * **`page` is still sent only above 1, and now for an ordinary reason**: it is
   * the server's own default, and this app's rule everywhere else is that a
   * default is spelled by the key's ABSENCE. Leaving it off keeps one canonical
   * request for the unpaged view. The half that was load-bearing and is now
   * merely tidy was left alone deliberately rather than churned.
   *
   * 🔴 **`search` GOES TO THE SERVER — it is the one filter on this page that
   * does.** `billingRegion` and `cycleId` are still applied in the browser and
   * each is blocked on something named at `visibleRows` and `visibleCycles`.
   * `listQuery` drops an empty value, so a cleared box sends no key at all.
   */
  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      return api
        .listRevenueTracker(
          {
            ...(page > 1 ? { page } : {}),
            ...(searchTerm !== '' ? { search: searchTerm } : {}),
            /*
             * ✅ **ON THE WIRE SINCE 2026-09-14.** The fifth enum member `none`
             * is what unblocked it: the control offers "No billing region
             * recorded" for the companies with no country of record, and until
             * the server could be ASKED for them, moving the four real slugs
             * and leaving the fifth would have made one control behave two ways
             * — four values narrowing the tracker, one narrowing a page, with
             * nothing on screen saying which.
             *
             * ⚠️ **VERIFIED ON THE LIVE WIRE, not read off the schema**:
             * `meta.total` 122 → 1 for `none` and → 10 for `europe`,
             * `meta.bandTotals` 5 → 1 on both, and `zzz` a 422 naming the key.
             */
            ...(regionKeys.length > 0 ? { billingRegion: regionKeys } : {}),
            /*
             * 🔴 **CONVERSION. See {@link REPORTED_IN_CURRENCY} for the owner's
             * decision and the cost it trades away.**
             *
             * ⚠️ **IT IS SENT UNCONDITIONALLY, AND THAT IS WHAT MAKES IT AGREE
             * WITH THE SHEET.** The mirror converts on every run with no request
             * to vary it, so a condition here — a picker, a saved preference, a
             * key omitted on page one — is a state in which the two views of one
             * dataset differ. There is no such condition and there must not be.
             *
             * ⚠️ **IT RIDES ON EVERY PAGE OF THE PAGED READ**, which this
             * builder gives for free because it runs per fetch. Page two
             * answered without it comes back UNCONVERTED, and those rows are
             * then read under the same heading as page one's converted ones —
             * dollars beside pounds, every figure well-formed, nothing failing.
             *
             * ⛔ **NO CASE FOLDING.** `^[A-Z]{3}$` is the whole rule and `'usd'`
             * is a 422 — and `f_currencies` has a case-insensitive collation, so
             * a lower-cased code would MATCH the row and be echoed back in a
             * spelling every other consumer compares against upper case.
             */
            baseCurrency: REPORTED_IN_CURRENCY,
          },
          signal,
        )
        .then((data) => {
          setResult(data);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof Error ? caught.message : 'Failed to load the revenue tracker');
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [page, searchTerm, regionKeys],
  );

  /*
   * ⚠️ **DEBOUNCED AT 250ms, THE HOUSE FIGURE, AND ON THE EFFECT RATHER THAN ON
   * THE VALUE** — `ContactsPage` is the reference this copies. `search` now
   * reaches the wire, so without this every keystroke would be a request against
   * the office's shared rate limit, on the widest read in the app.
   *
   * ⚠️ The timer restarts on any dependency of `load`, `page` included, so
   * pressing Next costs 250ms too. That is the reference pattern's own behaviour
   * and is left as it is: one debounce that cannot be forgotten beats two code
   * paths where the search box is the only one anybody remembers to throttle.
   */
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => void load(controller.signal), 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [load]);

  /*
   * ⚠️ **THE SAVED NOTE IS WRITTEN INTO THE ROW IN PLACE, and the list is NOT
   * re-read.** A re-read of a cross-tab this wide throws away the reader's
   * horizontal scroll position, which on a nineteen-column grid is most of what
   * they were looking at.
   *
   * `stored` is what the server said, where it said anything — see
   * `RevenueNotesField`. Matched on `key`, never on the company name: two live
   * companies share the display name `Think Test Company`.
   *
   * A cycle with no cell yet gains one, because the write is an upsert and a
   * note against an untraded cycle is expressible.
   */
  const applyNotes = useCallback(
    (rowKey: string, cycle: string, stored: string | null) => {
      setResult((current) => {
        if (!current) return current;
        const index = current.data.findIndex((row) => row.key === rowKey);
        if (index === -1) return current;
        const row = current.data[index];
        if (!row) return current;
        const data = current.data.slice();
        data[index] = {
          ...row,
          cycles: {
            ...row.cycles,
            [cycle]: { ...(row.cycles[cycle] ?? EMPTY_CELL), notes: stored },
          },
        };
        return { ...current, data };
      });
    },
    [],
  );

  /*
   * ⚠️ ONLY `meta.cycles` CAN FEED THIS -- never `allCycles`, which falls back
   * to the union of the rows own keys. That fallback carries NAMES and no ids,
   * so it can render a column and can never identify one to write against.
   *
   * An empty list is therefore the honest input when the server did not send
   * the key: the lookup misses, and the save REFUSES with a sentence saying
   * so, rather than posting a fabricated id that would file the note against a
   * different financial year and read back looking correct.
   */
  const writer = useRevenueNotesWriter(result?.meta?.cycles ?? []);

  /* ── filtering, in the browser ───────────────────────────────────────── */

  const rows = useMemo(() => result?.data ?? [], [result]);

  /*
   * 🔴 **THE COLUMNS COME FROM `meta.cycles`, WHICH IS ORDERED BY THE CYCLE'S
   * START DATE — never from a list written in this file, and never from the rows
   * where the server has answered.** A hardcoded list goes stale in April, when
   * the next cycle opens and its column never appears; and a column that is
   * simply absent looks exactly like a cycle nobody has traded in yet, so
   * nothing on the page would report it.
   *
   * ⚠️ The union of the rows' own keys is the FALLBACK for a server that
   * predates the key, and it is wrong in two silent ways `cycleKeysOf` records —
   * it drops a cycle nobody on this page traded in, and it orders by LABEL. An
   * empty `meta.cycles` is taken at its word (a tracker with no cycles yet); it
   * is the ABSENT key that falls back, which is why this tests `undefined`
   * rather than length.
   */
  const serverCycles = result?.meta?.cycles;
  const derivedCycles = useMemo(() => cycleKeysOf(rows), [rows]);
  /*
   * ⚠️ THE COLUMN LIST IS NAMES; THE WRITE NEEDS IDS. Both come from
   * `meta.cycles`, and they are taken from it SEPARATELY rather than one being
   * derived from the other downstream -- the grid is keyed by name, the
   * endpoint wants the id, and `meta.cycles` is the only thing that holds both
   * together. Mapping to names here keeps every renderer working in one
   * currency of identity.
   */
  const allCycles = useMemo<readonly string[]>(
    () => serverCycles?.map((c) => c.name) ?? derivedCycles,
    [serverCycles, derivedCycles],
  );

  /**
   * The bands the filter offers.
   *
   * 🔴 **READ FROM THE MASTER, NOT FROM THE RESPONSE THIS CONTROL FILTERS —
   * 2026-09-14, and it is what made moving the filter to the wire possible at
   * all.** *SUPERSEDED: "Every band on the page, for the filter. Built from the
   * rows, so it cannot offer nothing", derived from `rows`.*
   *
   * ⚠️ **THE OLD SHAPE WOULD HAVE COLLAPSED THE CONTROL THE MOMENT THE FILTER
   * WENT SERVER-SIDE.** With `?billingRegion=europe` on the wire, `rows` holds
   * Europe and nothing else — so a picker built from them would offer the one
   * band already chosen, and a reader could never move from one band to
   * another, only clear back to all. **That is the exact hazard written out at
   * the cycle filter below, which is still browser-side partly because of it.**
   *
   * ⚠️ **SO THE "cannot offer a band that matches nothing" PROPERTY IS SPENT,
   * DELIBERATELY.** A band with no companies is now reachable and answers an
   * empty table — the same trade `/companies`' country funnel makes, and the
   * cheaper of the two: an empty table under a band somebody picked is legible,
   * where a control that cannot reach the other four is not.
   *
   * ⚠️ **`none` IS APPENDED RATHER THAN READ.** It is a FILTER member, not a
   * band — the master holds one row per real region and `none` is not one of
   * them — and it is the population `d_companies.country_id` being nullable
   * produces. Its key must match what the server's enum calls it.
   *
   * ⚠️ **A FAILED MASTER READ FALLS BACK TO THE ROWS**, which is the old
   * behaviour: degraded (only the bands on this page) but usable, rather than a
   * picker with nothing in it beside a filter somebody may already hold.
   */
  const regionOptions = useMemo(() => {
    const fromMaster = regionMaster.map((region) => ({
      value: region.slug as string,
      label: region.name,
    }));

    if (fromMaster.length > 0) {
      return [...fromMaster, { value: 'none', label: 'No billing region recorded' }];
    }

    const seen = new Map<string, string>();
    for (const row of rows) {
      if (!row.billingRegion) {
        seen.set('none', 'No billing region recorded');
        continue;
      }
      seen.set(row.billingRegion.slug, row.billingRegion.name);
    }
    // A selected band the rows do not carry stays tickable off.
    for (const key of regionKeys) {
      if (!seen.has(key)) seen.set(key, key === 'none' ? 'No billing region recorded' : key);
    }
    return [...seen].map(([value, label]) => ({ value, label }));
  }, [regionMaster, rows, regionKeys]);

  /*
   * ⚠️ **A CYCLE'S NAME IS ITS OWN VALUE HERE, AND THAT IS NOT AN OVERSIGHT.**
   * `allCycles` is names because the grid is KEYED by name — `cellOf` looks a
   * row's cell up by it, and `?cycle=` in the URL is that same name. The ids
   * sit on `meta.cycles` and are taken from it separately for the write; a
   * picker that sent one here would be filtering a client-side grid by a key
   * nothing on the page is keyed by, and every column would empty with nothing
   * failing. `value === label` is the honest shape for that, not a shortcut.
   *
   * Built here rather than inline so the identity is stable across renders:
   * `Combobox` holds `options` in a `useMemo` dependency, and a fresh array
   * every render re-filters the open popover on every keystroke elsewhere.
   */
  const cycleFilterOptions = useMemo(
    () => {
      // A selected cycle the response does not carry stays tickable off.
      const known = new Set(allCycles);
      return [...cycleKeys.filter((cycle) => !known.has(cycle)), ...allCycles].map((cycle) => ({
        value: cycle,
        label: cycle,
      }));
    },
    [allCycles, cycleKeys],
  );

  /*
   * ⚠️ **THE CYCLE FILTER STAYS IN THE BROWSER, AND IT IS THE ONE THAT IS SAFE
   * THERE** — it hides COLUMNS, not rows, so no row is missing from another page
   * and the page-scope objection that moved `search` does not apply to it.
   *
   * ⛔ **AND MOVING IT WOULD COST A CAPABILITY, WHICH IS THE STRONGER REASON.**
   * `?cycleId=` narrows the columns server-side as well — the shapes agree — but
   * the repository calls `listCycles(options.cycleId)`, so **`meta.cycles` comes
   * back holding only the cycle asked for.** `allCycles` is built from it, so the
   * picker's own option list would collapse to the one cycle already chosen and
   * a reader could not switch from one cycle to another, only clear back to all.
   *
   * ⚠️ **A second, independent blocker: the wire wants an ID and this grid is
   * keyed by NAME.** `cellOf` looks a cell up by name, `?cycle=` in the URL is
   * that name, and the ids live on `meta.cycles` — which arrives WITH a response,
   * so a cold load on a shared `?cycle=2025 - 26` link has no id to send. Sending
   * the name is a 422: the key is `z.coerce.number()` and coercing `2025 - 26`
   * yields `NaN`, which is the same mistake that once broke every note save.
   *
   * Both would be answered by a cycle list this page can read independently of
   * the response it is filtering — the *read the master, never the response you
   * are filtering* rule. Neither is answered by sending the key.
   */
  const visibleCycles = useMemo(
    () => {
      if (cycleKeys.length === 0) return allCycles;
      const wanted = new Set(cycleKeys);
      return allCycles.filter((c) => wanted.has(c));
    },
    [allCycles, cycleKeys],
  );

  /*
   * 🔴 **THE COMPANY SEARCH IS GONE FROM HERE — IT IS A REQUEST KEY SINCE
   * 2026-09-13.** *SUPERSEDED: a second clause in this filter reading
   * `if (term !== '' && !row.company.name.toLowerCase().includes(term))`.* It
   * narrowed the page rather than the tracker, so a company on page 3 could not
   * be found from page 1 — see `RevenueTrackerListParams.search`.
   *
   * ⛔ **DO NOT RE-ADD IT AS A BELT-AND-BRACES PASS.** The server matches
   * `c.name LIKE` under a case-insensitive collation and escapes `%`; a second
   * `includes()` here would agree on ordinary terms and disagree on a typed `%`
   * — the browser would drop a row the server correctly returned, silently.
   *
   * ✅ **THE BILLING REGION IS A REQUEST KEY TOO SINCE 2026-09-14, AND THE
   * BACKEND ASK IT WAS BLOCKED ON LANDED.** *SUPERSEDED: "⚠️ **THE BILLING
   * REGION STAYS IN THE BROWSER, AND IT IS BLOCKED ON ONE THING: `'none'` IS
   * INEXPRESSIBLE ON THE WIRE.** `?billingRegion=` is a
   * `z.enum(['united_kingdom','europe','rest_of_world','north_america'])` … What
   * unblocks it is a way to ask for the unclassified companies — a backend ask,
   * and nothing this page can synthesise."*
   *
   * The enum has a fifth member, `none`, so the unclassified companies can be
   * asked for and the control no longer behaves two ways. **What it bought: a
   * company in Europe sitting on page 3 is now found by filtering to Europe from
   * page 1**, where before the band narrowed only the page in hand.
   *
   * ⚠️ **TWO THINGS MOVED WITH IT, AND NEITHER IS COSMETIC** — `setRegion`
   * resets the page, and `regionOptions` reads the MASTER rather than these
   * rows, which would otherwise collapse to the band already chosen.
   *
   * ⚠️ **THE CYCLE IS STILL BROWSER-SIDE AND IS NOT NEXT.** It hides COLUMNS
   * rather than rows, so the page-scope objection never applied to it, and both
   * of its own blockers stand: `meta.cycles` comes back holding only the cycle
   * asked for, and the wire wants an id this grid does not key by. See below.
   */
  const visibleRows = rows;

  const groups = useMemo(() => groupByRegion(visibleRows), [visibleRows]);

  const meta = result?.meta;
  const totalPages = Math.max(1, meta?.totalPages ?? 1);
  const paginated = meta?.totalPages !== undefined && totalPages > 1;
  /*
   * 🔴 **PAID REVENUE IN NO CYCLE WINDOW, AND THEREFORE IN NO COLUMN.** Every
   * figure on the grid is real; the grid is simply SHORT, by an amount only this
   * number reports. Unsaid, a finance reader totting up the columns gets a
   * smaller answer than the accounts and has nothing on screen to find out why —
   * no wrong cell to look at, no error to read.
   *
   * ⚠️ `0` and ABSENT are different and only one of them is a clean bill of
   * health: absent means this server does not report it. Neither renders the
   * banner, because there is nothing actionable to say about a server that
   * cannot answer — but do not let that make `?? 0` look correct here.
   */
  const unmatchedLines = meta?.unmatchedLines ?? 0;
  /*
   * 🔴 **THE SERVER'S ECHO, AND EVERY UNIT ON THIS PAGE IS LABELLED FROM IT
   * RATHER THAN FROM {@link REPORTED_IN_CURRENCY}.**
   *
   * What was ASKED FOR and what the server MEASURED IN are two facts. A server
   * predating `baseCurrency` ignores the key and answers an ordinary `200` of
   * unconverted rows — so a page that labelled itself from the constant would
   * state a unit the figures are not in **on exactly the deploy where nobody is
   * expecting it**, with every number well-formed and nothing failing. That is
   * the one failure `meta.measuredInCurrency` exists to make impossible.
   *
   * ⚠️ **`?? null` AND NEVER `?? REPORTED_IN_CURRENCY`.** `undefined` (this
   * server has no such key) and `null` (nothing was converted) are the same fact
   * for a reader — *there is no conversion to label* — and both must fall through
   * to the page this repo shipped yesterday. Defaulting to what was asked for
   * erases precisely the state the field reports.
   */
  const measuredInCurrency = meta?.measuredInCurrency ?? null;
  /*
   * 🔴 **PAID LINES NO RATE COULD PRICE — THE ONLY THING ON THE WIRE THAT CAN
   * SAY THIS REPORT IS SHORT.** A line with no exchange rate for its own bill
   * date is left OUT of every converted figure and counted here; it is not
   * converted at par, because a dollar counted as a pound is a wrong number with
   * nothing beside it.
   *
   * ⛔ **`?? null`, NEVER `?? 0`.** On a field whose job is to report a GAP the
   * fallback IS the lie: `0` is *we converted and lost nothing*, absent is
   * *nobody looked*, and folding the second into the first turns an unexamined
   * report into a confident all-clear. This is the `use-revenue-products.ts`
   * defect written down rather than repeated.
   *
   * ⚠️ **`0` IS THE HEALTHY ANSWER AND IS WHAT PRODUCTION GIVES TODAY**, which
   * is exactly why the notice has to exist: a figure that is only ever right is
   * one nobody builds a reader for, and the day a rate gap appears this page
   * would otherwise under-report in silence.
   */
  const unpricedLines = meta?.unpricedLines ?? null;
  const initialLoad = loading && result === null;
  /** Any filter at all — what the empty state and the Clear button answer to. */
  const filtered = searchTerm !== '' || regionKey !== ALL || cycleKey !== ALL;
  /*
   * 🔴 **ONLY THE FILTERS THAT REMOVE ROWS *FROM WHAT CAME BACK*, WHICH IS NOW
   * THE REGION ALONE.** It drives the `N of M` in the count line, and getting
   * this wrong is how that line starts lying:
   *
   *  - **`searchTerm` MUST NOT be in here any more.** It narrows on the server,
   *    so `rows` is already the searched population and the line would read
   *    *"5 companies of 5"* — a ratio between a number and itself, presented as
   *    evidence that a filter did something.
   *  - **`cycleKey` was never right in here** and is left out: it hides COLUMNS,
   *    so `visibleRows` and `rows` are the same length under it and the clause
   *    printed *"98 of 98"*. The cycle count after the `·` is what reports it.
   *
   * 🔴 **AND AS OF 2026-09-14 THERE IS NOTHING LEFT IN IT.** *SUPERSEDED:
   * `const pageFiltered = regionKey !== ALL;`* The band narrows on the SERVER
   * now, so `rows` is already the banded population and the clause would read
   * *"10 companies of 10"* — a ratio between a number and itself, offered as
   * evidence that a filter did something. That is the identical defect the
   * `searchTerm` bullet above describes, arriving by the identical route.
   *
   * ⚠️ **KEPT AS A NAMED CONSTANT RATHER THAN DELETED WITH ITS CLAUSE**, so the
   * next filter that narrows what came back has an obvious place to go and the
   * reasoning above stays attached to it.
   */
  const pageFiltered = false;

  /* ── the closing TOTAL rows ──────────────────────────────────────────── */

  /*
   * ⚠️ **BOTH ARE OPTIONAL AND ARE READ WITH `?.`, LIKE EVERY OTHER MEMBER OF
   * THIS ENVELOPE.** This app deploys separately from the API and can be newer
   * than it, so a server that computes no totals is an ORDINARY answer: the
   * table then renders exactly as it did before they existed. Declaring either
   * required would make such a server a `TypeError` inside a render, which React
   * escalates to unmounting the tree — a blank page because a total could not be
   * drawn.
   *
   * They are read INDEPENDENTLY because they are sent independently: a server
   * may band-total without grand-totalling, or the reverse.
   */
  const bandTotals = meta?.bandTotals;
  const grandTotals = meta?.grandTotals;

  /*
   * ⚠️ Matched on `bandKeyOf`, the SAME function the rows are grouped with, so
   * the two spellings cannot come apart. A lookup that missed would not fail —
   * every band total would simply vanish, which looks exactly like a server that
   * does not send them.
   */
  const bandTotalByKey = useMemo(() => {
    const map = new Map<string, RevenueTrackerBandTotal>();
    for (const entry of bandTotals ?? []) {
      const key = bandKeyOf(entry.billingRegion);
      // First wins. A response naming one band twice is the server's to
      // reconcile; merging two figures here would be this page doing arithmetic.
      if (!map.has(key)) map.set(key, entry);
    }
    return map;
  }, [bandTotals]);

  /*
   * ⚠️ **A REGION THAT APPEARS IN TWO NON-ADJACENT RUNS GETS NO TOTAL.**
   * `groupByRegion` bands CONSECUTIVE runs and never reorders, deliberately — so
   * a response that interleaves regions renders that region as two bands, and
   * the one band total the server sent describes neither of them on its own.
   * Printing it under both would show one figure twice, each time claiming to
   * close rows it only partly covers. Rare, and silent if it ever happened.
   */
  const repeatedBandKeys = useMemo(() => {
    const counts = new Map<string, number>();
    for (const group of groups) counts.set(group.key, (counts.get(group.key) ?? 0) + 1);
    return new Set([...counts].filter(([, count]) => count > 1).map(([key]) => key));
  }, [groups]);

  const bandTotalFor = useCallback(
    (key: string) => (repeatedBandKeys.has(key) ? undefined : bandTotalByKey.get(key)),
    [repeatedBandKeys, bandTotalByKey],
  );

  /*
   * 🔴 **A COMPANY SEARCH SUPPRESSES THE TOTALS ROWS, AND NOTHING ELSE DOES.**
   * The totals are computed on the SERVER; this page filters in the BROWSER.
   * Work through the three filters and only one of them breaks the agreement:
   *
   *  - **Billing region** hides whole BANDS. A surviving band still holds every
   *    company it had, so its own total is still exact. Nothing to suppress.
   *  - **Cycle** hides COLUMNS, not rows. Every column still on screen still
   *    holds every row it did, so its total is still exact.
   *  - **Search** removes companies from WITHIN a band — and a band total then
   *    describes rows that are no longer on screen. It is the one case that
   *    produces an authoritative-looking figure disagreeing with what is above
   *    it, which on a finance screen is worse than no figure at all.
   *
   * ⚠️ **THE SENTENCE BESIDE IT IS NOT HELPER TEXT.** Rows that were there a
   * keystroke ago and are gone now, with nothing said, read as a rendering
   * fault; it names a state the reader cannot otherwise see, which is the
   * documented exception to this app's no-helper-text rule.
   *
   * ────────────────────────────────────────────────────────────────────────
   * 🔴 **SUPERSEDED IN PART, 2026-09-13: THE SEARCH BULLET ABOVE NO LONGER
   * DESCRIBES A BROWSER-SIDE FILTER, AND THE SUPPRESSION IS KEPT ANYWAY.**
   *
   * `search` is a request key now, and the backend's `listBandTotals` honours
   * the same `companyFilter` and the same `COMPANY_POPULATION` as the page read
   * and its `COUNT(*)` — its own header says *"`search`, `companyId` and
   * `billingRegion` change WHICH ROWS THE READER IS LOOKING AT … a total that
   * ignored them would be a correct total of a different report."* **So on that
   * reading the totals are now EXACT under a search and this suppression hides
   * correct figures.**
   *
   * ⛔ **IT IS STILL NOT LIFTED, AND THE ASYMMETRY IS THE WHOLE ARGUMENT.** That
   * is a SOURCE READ of another repo, not a measurement from the wire — there
   * are no credentials here — and the two errors do not cost the same: hiding a
   * correct total is visibly missing and recoverable, while showing a wrong one
   * puts an authoritative figure on a finance screen that disagrees with the
   * rows above it and nobody reports it. **This repo has a rule for that: fail
   * towards the visible wrong answer.**
   *
   * ✅ **WHAT LIFTS IT is one measurement, not a re-reading of the server**: with
   * a token, search a term that removes companies from a band and check the
   * band's `Total` against the rows still on screen. If it agrees, delete
   * `searching` from `showTotals` and this block with it.
   *
   * ⚠️ **THE REGION AND CYCLE BULLETS ARE UNTOUCHED AND STILL TRUE**, because
   * both of those filters really are still applied in the browser.
   *
   * ✅ **AND THE OLD OPEN QUESTION IS ANSWERED — the paging half, read in backend
   * source the same day.** *SUPERSEDED: "**UNRESOLVED, AND DELIBERATELY NOT ACTED
   * ON: WHETHER THESE FIGURES COVER THE WHOLE TRACKER OR ONE PAGE OF IT.** The
   * envelope's paging half was never published and `meta.totalPages` has not been
   * observed, so the question has not arisen."* It has arisen — the server
   * defaults `perPage` to 50 against 400-odd companies, so `meta.totalPages` is
   * routinely above 1 and the pager renders. **`listBandTotals` takes no `LIMIT`
   * and the backend states it outright: *"Paging is the one thing that changes
   * neither."*** So a totals row covers the whole FILTERED population, not the
   * page beneath it.
   *
   * ⚠️ **THAT IS A REAL, PRE-EXISTING DISAGREEMENT AND IT IS NOT FIXED HERE.** On
   * page 2 of 3 the band total closes rows the reader cannot see. It is left
   * alone rather than guessed at: suppressing on `paginated` would hide every
   * total on a tracker that is nearly always paged, and the sentence that would
   * make it honest is helper text the owner removed on 2026-09-05. **It is an
   * owner call — raise it; do not settle it in a filters pass.**
   */
  const searching = searchTerm !== '';
  const hasTotals = bandTotals !== undefined || grandTotals !== undefined;
  const showTotals = hasTotals && !searching;

  /*
   * 🔴 **THE QUALIFIER IS GONE, BECAUSE THE FILTER MOVING TO THE WIRE MADE IT
   * FALSE — 2026-09-14.** *SUPERSEDED: "⚠️ **THE GRAND TOTAL IS NOT SUPPRESSED
   * BY THE REGION FILTER — IT IS LABELLED.** It closes every band, so with one
   * band on screen it is no longer 'the table's total' … `const
   * grandTotalQualifier = regionKey === ALL ? undefined : 'across every billing
   * region';`"*
   *
   * That sentence was exactly right while the band was narrowed in the BROWSER:
   * the server totalled everything and the reader was shown a slice, so the
   * grand total really did cover bands that were not on screen. **The server
   * narrows now** — `meta.grandTotals` under `?billingRegion=europe` covers
   * Europe and nothing else, measured on the wire — so the phrase would claim a
   * scope the figure no longer has. **A qualifier that survives the thing it
   * qualified is worse than none: it is a confident, specific, wrong statement
   * about money.**
   *
   * ⚠️ **This is the failure this repo keeps recording — an artefact accurate
   * about what it described, falsified by a change somewhere else, with nothing
   * failing.** It was found by asking what each reader of `regionKey` assumed,
   * not by anything going red.
   */
  const grandTotalQualifier = undefined;

  const countLabel = initialLoad
    ? 'Loading…'
    : `${visibleRows.length} ${visibleRows.length === 1 ? 'company' : 'companies'}` +
      (pageFiltered ? ` of ${rows.length}` : '') +
      ` · ${visibleCycles.length} ${visibleCycles.length === 1 ? 'cycle' : 'cycles'}`;

  /*
   * ⚠️ **IT RESETS THE PAGE, because it clears `q` and `q` is a SERVER filter.**
   * Clearing a search widens the result, so the reader's page number is a page of
   * a set that no longer exists — and the widened answer's page 5 holds companies
   * that have nothing to do with what they were just looking at. One reset covers
   * every key here; see `setSearch`.
   */
  function clearFilters() {
    const next = new URLSearchParams(searchParamsKey);
    next.delete('q');
    next.delete('region');
    next.delete('cycle');
    setSearchParams(next, { replace: true });
    setPage(1);
  }

  const emptyState = (
    <EmptyState
      title={
        error
          ? 'The revenue tracker could not be loaded'
          : filtered
            ? 'No companies match these filters'
            : 'Nothing on the revenue tracker yet'
      }
      description={
        error
          ? 'The message above says what the server reported.'
          : filtered
            ? 'Clear the filters to see every company again.'
            : 'Revenue appears here as quotes are accepted and invoices are raised.'
      }
      {...(filtered
        ? {
            action: (
              <Button variant="secondary" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            ),
          }
        : {})}
    />
  );

  /*
   * ⚠️ Below every hook, so the early return cannot change the hook order. The
   * refusal is rendered here rather than by the route, which carries no guard —
   * every signed-in route in this app is an ordinary `Shell` route, so a reader
   * arriving by URL must get a sentence rather than an error or a blank screen.
   */
  if (!canReadSurface) {
    return (
      <>
        <PageHeader eyebrow="Trackers" title="Revenue" />
        <Panel className="min-w-0">
          <EmptyState
            title="This tracker isn’t available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </>
    );
  }

  /*
   * ⚠️ **ONE EDIT FORM FOR EVERY INVOICE NUMBER ON THE PAGE, mounted at the
   * page rather than in the cell.** This cross-tab renders a service line per
   * company per cycle, so a modal per cell would be a mounted form per invoice
   * number on screen. See `InvoiceQuickEdit`.
   *
   * 🔴 **`load`, NOT a local patch, and this is the one place that differs from
   * the notes editor above.** `applyNotes` writes a saved note straight into the
   * row precisely so a re-read cannot throw away the reader's horizontal scroll
   * position — right for a note, which changes one cell and no figure. An
   * invoice save changes the FIGURES: every amount here is a paid invoice line,
   * so a currency, a status or a line edit moves the cells, the band totals and
   * which companies are on the page at all. There is nothing to patch in, and
   * leaving the grid as it was is silent.
   *
   * ⚠️ **THE NOTICES IT RENDERS SIT ABOVE THE HEADER**, which is where this
   * wrapper puts them. That is deliberate rather than incidental: a report that
   * Xero would not take a change is the only artefact anywhere carrying that
   * fact, and the reload it follows can take the row it came from off the page.
   */
  return (
    <InvoiceQuickEdit onSaved={() => void load()}>
      <PageHeader
        eyebrow="Trackers"
        title="Revenue"
        actions={
          <>
            {/*
             * The toggle renders only when there is a second view to reach. A
             * lone "Table" chip with nothing to switch to would be an
             * affordance whose only outcome is nothing happening.
             */}
            {/*
             * ⚠️ The DEFAULT is spelled by the key's ABSENCE, and the default is
             * now the sheet — so choosing `sheet` CLEARS the param and choosing
             * `table` writes one. That is the inverse of what this line did
             * before 2026-09-02, and getting it backwards leaves a canonical URL
             * per view that names the view nobody is looking at.
             */}
            {canShowSheet && <ViewToggle view={view} onChange={(next) => setParam('view', next === 'table' ? 'table' : '')} />}
            {/*
             * ⚠️ KEPT ON BOTH VIEWS, DELIBERATELY. The frame is a mirror, so
             * the way to actually work in the sheet is this button — removing
             * it on the Sheet tab would make the embed a dead end, and leaving
             * it on the Table tab keeps the document one click away without
             * anybody having to discover the tab first.
             *
             * Absent, never disabled: no mirror configured, the answer not yet
             * known, and a server with no such route all mean the same thing to
             * a reader.
             *
             * 🔴 **THERE IS NO `Sync now` BESIDE IT AND THERE MUST NOT BE** —
             * owner's instruction, *"we only support auto sync"*. The freshness
             * line inside the mirror card is the other half of that decision
             * and is NOT part of the removal.
             */}
            {sheetOpenUrl !== null && (
              <Button
                size="sm"
                variant="secondary"
                href={sheetOpenUrl}
                target="_blank"
                trailingIcon={<ExternalTabIcon />}
              >
                Open in Google Sheets
                <span className="sr-only"> (opens in a new tab)</span>
              </Button>
            )}
          </>
        }
      />

      {resolvingSheet ? (
        <RevenueMirrorPanel sheet={sheet}>
          <RevenueSheetPlaceholderPanel />
        </RevenueMirrorPanel>
      ) : view === 'sheet' && sheetEmbedUrl !== null ? (
        /*
         * 🔴 **THE FRESHNESS LINE IS THE MIRROR CARD'S OWN FOOTER AND RENDERS
         * ON THIS BRANCH ONLY.** On the sibling dashboard it floated loose above
         * whatever panel was on screen and, on the table view, described a
         * document that was not there — the owner's *"it looks submerged"*. The
         * gating is structural: the table branches below never mount
         * `RevenueMirrorPanel`, so there is no condition for anybody to delete.
         */
        <RevenueMirrorPanel sheet={sheet}>
          <TrackerSheetPanel
            embedUrl={sheetEmbedUrl}
            openUrl={sheet.link}
            /*
             * ⚠️ **Named for THIS tracker, not left at the default.** The prop
             * defaults to "Client Deliverables tracker in Google Sheets" — the
             * accessible name of the frame — so leaving it out would announce the
             * wrong document to a screen reader while showing the right one.
             */
            frameTitle="Revenue Tracker in Google Sheets"
          />
        </RevenueMirrorPanel>
      ) : (
        <Panel>
          <PanelHeader
            title="Revenue by company and cycle"
            description={countLabel}
          />

          <div className="border-b border-border px-4 py-3">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {/*
                * 🔴 **THIS BOX NOW ASKS THE SERVER, and the other two filters in
                * this bank do not.** It goes out as `?search=`, debounced at
                * 250ms, and it RESETS THE PAGE — `setSearch`, never `setParam`
                * directly, which is the whole of the `/users` defect in one
                * habit.
                *
                * ⚠️ **`maxLength={200}` IS THE SERVER'S CAP, NOT A LAYOUT
                * CHOICE.** `search` is `.max(200)` and a longer term is a **422**
                * that takes the entire table out, not one control — so the
                * refusal is made unreachable here rather than handled below.
                * Move the two together or neither.
                *
                * ⚠️ **THE LABEL SAYS "companies" AND MUST KEEP SAYING IT.** The
                * server matches `d_companies.name` and nothing else — not a
                * service name, not an invoice number, not a billing region — so
                * "Search" alone would promise the row a reader can see rather
                * than the one column that is actually matched.
                */}
              <Field label="Search companies">
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    type="search"
                    placeholder="Search companies…"
                    aria-describedby={describedBy}
                    maxLength={200}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                )}
              </Field>

              {/*
                * ✅ **BOTH FILTERS ARE SEARCHABLE `Combobox`es — owner's
                * decision 2026-09-13.** *SUPERSEDED: two `ui/Select`s, each with
                * an inline `<option>` list.* The owner chose ONE filter shape
                * across the app rather than a per-control judgement about
                * whether a list is short enough to scan, so these match the
                * bank on `/deals` instead of being the two that did not.
                *
                * ⚠️ **`emptyOptionLabel` AND `placeholder` CARRY THE SAME
                * STRING, and both are required.** `Combobox` resolves the
                * trigger's label out of `options` ALONE, so the "no selection"
                * row it synthesises is never `selected` — the trigger falls back
                * to `placeholder` for `''`. Pass only one and the control reads
                * `Select…` over a filter that is genuinely clearing nothing, or
                * offers no way back to unfiltered at all.
                *
                * ⚠️ **THE WORDING IS UNCHANGED, deliberately.** `''` is what
                * `setParam` deletes the key for, and these two sentences are
                * what the URL's absent key has always read as on screen.
                * Rewording them here would rename a state, not a control.
                *
                * ⚠️ **NO `loading` AND NO `invalid` ON EITHER, and that is not
                * an omission copied past.** Both lists are derived from `rows`
                * — the response this page has already rendered — so there is no
                * second fetch to be in flight and none to fail: while the table
                * is loading there is no filter bank on screen at all. A
                * `loading` here would describe a request that does not exist.
                */}
              <Field label="Billing region">
                {({ id, 'aria-describedby': describedBy }) => (
                  <MultiCombobox
                    id={id}
                    aria-describedby={describedBy}
                    values={regionKeys}
                    /* ⚠️ `setRegion`, NOT `setParam` — it is a request key now,
                     * so it must reset the page. See `setRegion`. */
                    onChange={setRegion}
                    options={regionOptions}
                    placeholder="All billing regions"
                    searchPlaceholder="Search billing regions…"
                  />
                )}
              </Field>

              <Field label="Cycle">
                {({ id, 'aria-describedby': describedBy }) => (
                  <MultiCombobox
                    id={id}
                    aria-describedby={describedBy}
                    values={cycleKeys}
                    onChange={(values) => setParam('cycle', values.join(','))}
                    options={cycleFilterOptions}
                    placeholder="All cycles"
                    searchPlaceholder="Search cycles…"
                  />
                )}
              </Field>

              <div className="flex items-end">
                <Button
                  variant="secondary"
                  className="w-full sm:w-auto"
                  disabled={!filtered}
                  onClick={clearFilters}
                >
                  Clear filters
                </Button>
              </div>
            </div>

            {/*
             * ⚠️ *SUPERSEDED 2026-09-13: "**SAID IN WORDS BECAUSE A FILTER THAT
             * NARROWS ONE PAGE READS EXACTLY LIKE ONE THAT NARROWED THE WHOLE
             * TRACKER.** The endpoint publishes no query schema … so these three
             * filter what came back rather than what was asked for."*
             *
             * **The endpoint DOES publish a query schema** (measured 2026-09-11)
             * and **`search` is now sent to it**, so "these three" is two. The
             * words that sentence describes were already gone — removed with the
             * rest of this app's helper text on 2026-09-05, see the note below —
             * so it had been describing a line that did not exist for a week.
             *
             * ⚠️ **THE HAZARD IT NAMED IS REAL AND STILL LIVE FOR THE OTHER TWO**:
             * the region filter narrows one page of up to 100 companies out of
             * 400-odd, and that is unsaid. Its blocker is written at
             * `visibleRows`. **Do not close it by reinstating a sentence here** —
             * that reverses an owner ruling; close it by moving the filter.
             */}
            {/*
             * ⚠️ **TWO NOTES WERE REMOVED HERE — owner's ruling, 2026-09-05**,
             * with the rest of this app's helper text: the page-scope note
             * ("These filters narrow the companies on this page only — page N of
             * M.") and the totals-suppression note ("Closing totals are hidden
             * while a company search is running…").
             *
             * ⚠️ **THE SUPPRESSION ITSELF IS UNTOUCHED AND IS THE PART THAT
             * MATTERS.** `showTotals` still hides every closing total while a
             * company search is running, because a search takes companies out of
             * a band and a band total would otherwise describe rows that are no
             * longer above it. What is gone is the sentence saying so — so the
             * rows now vanish silently. **Do not "fix" that by letting the
             * totals render through a search.**
             */}
          </div>

          {/*
           * 🔴 **THE COLUMNS DO NOT SUM TO THE BUSINESS'S REVENUE, AND THIS IS
           * THE ONLY PLACE THAT SAYS SO.** See `unmatchedLines` above.
           *
           * ⚠️ **A NOTICE, NOT AN ERROR.** Nothing failed and nothing on the
           * grid is wrong — the tone is `warning` rather than `danger` because a
           * red band would send somebody looking for a broken page instead of
           * for an invoice dated outside every cycle window.
           *
           * ⚠️ It renders on the TABLE view only, structurally: the sheet
           * branches above never reach here. A sentence about columns, shown
           * beside a spreadsheet whose columns are not ours, is the "submerged"
           * complaint in another costume.
           */}
          {/*
           * 🔴 **THE FIGURES ABOVE ARE CONVERTED, AND NOTHING ELSE ON SCREEN
           * SAYS SO.** A total summed from native amounts and one restated
           * through a reference rate render identically — same glyphs, same
           * column, same currency code — so the wrong conclusion this prevents,
           * *"this total is what the accounts will show"*, is one a finance
           * reader acts on. See {@link REPORTED_IN_CURRENCY} for the decision.
           *
           * ⛔ **NOT HELPER TEXT, AND A SWEEP MUST NOT TAKE IT.** It states a
           * FACT ABOUT THE DATA the reader cannot otherwise see — the documented
           * exception — and it is attached to FIGURES rather than to a control.
           * It is the same class as `Mixed currencies` on a band, one scale up.
           *
           * ⚠️ **GATED ON THE ECHO, NEVER ON {@link REPORTED_IN_CURRENCY}.** On
           * a server that ignored the key nothing was converted, and this
           * sentence would then be a false statement about how these figures
           * were produced — which is precisely the reason it was forbidden on
           * this tab until today.
           */}
          {measuredInCurrency !== null && (
            <p
              className="border-b border-border px-4 py-2 text-2xs text-fg-muted"
              title={CONVERSION_APPROXIMATE_NOTE.detail}
            >
              {CONVERSION_APPROXIMATE_NOTE.short}
            </p>
          )}

          {/*
           * 🔴 **REVENUE THAT COULD NOT BE PRICED, AND IS THEREFORE IN NO FIGURE
           * ABOVE.** The sibling of `unmatchedLines`: that one is work that
           * landed in no cycle COLUMN, this is work that could not be put into
           * the one UNIT. **A line can be neither, either or both**, so the two
           * are two sentences and adding them would report a smaller gap than
           * the report has.
           *
           * ⚠️ **IT NAMES MISSING REFERENCE DATA, NOT A CURRENCY POLICY.** *"In
           * another currency"* names something nobody can act on; *"no exchange
           * rate for its own date"* names a row somebody can backfill in
           * `f_exchange_rates`. The two send a reader to opposite places.
           *
           * ⚠️ **`> 0`, so `null` and `0` both render nothing** — and for
           * different reasons that must not be folded: `0` is a clean bill of
           * health and `null` is an unexamined report, and there is nothing
           * actionable to say about a server that did not look.
           */}
          {unpricedLines !== null && unpricedLines > 0 && (
            <Notice tone="warning" role="status" className="mx-4 mt-3">
              {/*
               * ⚠️ **BOTH HALVES OF THE SENTENCE AGREE IN NUMBER**, which is
               * only worth saying because the two clauses are chosen separately
               * and a mixed pair — "line has … their own dates" — reads as a
               * broken string rather than as a count of one.
               */}
              <span className="font-semibold">
                {unpricedLines} paid{' '}
                {unpricedLines === 1
                  ? 'line has no exchange rate for its own date, so it is'
                  : 'lines have no exchange rate for their own dates, so they are'}{' '}
                in none of the figures above.
              </span>
            </Notice>
          )}

          {unmatchedLines > 0 && (
            <Notice tone="warning" role="status" className="mx-4 mt-3">
              {/*
               * ⚠️ **THE COUNT STAYS AND ITS EXPLANATION IS GONE — owner's
               * ruling, 2026-09-05.** The second half read "Their invoice dates
               * sit in no cycle window, so they appear in no column below and the
               * columns do not add up to total revenue." That was the only place
               * saying the columns do not sum to the business's revenue; it is
               * still TRUE and is now unsaid.
               */}
              <span className="font-semibold">
                {unmatchedLines} paid{' '}
                {unmatchedLines === 1 ? 'line falls' : 'lines fall'} outside every cycle.
              </span>
            </Notice>
          )}

          <p role="status" aria-live="polite" className="sr-only">
            {countLabel}
          </p>

          {error && (
            <Notice
              tone="danger"
              role="alert"
              className="mx-4 mt-3"
              title={<>Couldn&rsquo;t load the revenue tracker.</>}
            >
              <span className="[overflow-wrap:anywhere]">{error}</span>
            </Notice>
          )}

          <div
            aria-busy={loading || undefined}
            className={cx(
              'transition-opacity duration-150 ease-out motion-reduce:transition-none',
              loading && result && 'opacity-60',
            )}
          >
            {/* Below lg a nineteen-column cross-tab cannot fit 320px. */}
            <div className="lg:hidden">
              {initialLoad ? (
                <div className="grid gap-3 p-4">
                  {Array.from({ length: 4 }, (_, index) => (
                    <Skeleton key={index} className="h-24 w-full" />
                  ))}
                </div>
              ) : groups.length === 0 ? (
                emptyState
              ) : (
                <>
                {groups.map((group, groupIndex) => {
                  // Resolved exactly as the table resolves it — one helper, so
                  // the two views cannot disagree about which bands close.
                  const bandTotal = bandTotalFor(group.key);
                  return (
                  <section key={group.key}>
                    {/*
                     * ⚠️ **THE SAME BAND TONE AS THE TABLE, FROM THE SAME INDEX
                     * INTO THE SAME ARRAY.** The two views show one page's data
                     * and a reader crossing the `lg` breakpoint must not have to
                     * relearn which band is which — so the tone is taken here
                     * exactly as the table takes it, rather than being a second
                     * palette that agrees today.
                     */}
                    <h2
                      className={cx(
                        'm-0 flex items-baseline gap-2 border-b border-border px-4 py-2',
                        bandTone(groupIndex).heading,
                      )}
                    >
                      <span className="min-w-0 truncate text-xs font-semibold text-fg">
                        {group.label}
                      </span>
                      {bandUnitOf(group, measuredInCurrency) !== null && (
                        <span className="shrink-0 text-2xs text-fg-muted">
                          {bandUnitOf(group, measuredInCurrency)}
                        </span>
                      )}
                      <span className="shrink-0 text-2xs text-fg-muted tabular">
                        {group.rows.length}{' '}
                        {group.rows.length === 1 ? 'company' : 'companies'}
                      </span>
                    </h2>
                    {group.rows.map((row) => (
                      <CompanyCard
                        key={row.key}
                        row={row}
                        cycles={visibleCycles}
                        writer={writer}
                        onNotesSaved={applyNotes}
                      />
                    ))}
                    {/*
                     * ⚠️ **THE BAND CLOSES HERE TOO.** The table and the cards
                     * show one page's data, and a figure that exists in one view
                     * and not the other is indistinguishable from a figure that
                     * was removed — `CompanyCard`'s own header states that rule
                     * for the notes editor and it applies at least as hard to
                     * the number the workbook's readers open the page for.
                     */}
                    {showTotals && bandTotal !== undefined && (
                      <TotalsCard
                        variant="band"
                        label="Total"
                        qualifier={group.label}
                        cycles={visibleCycles}
                        totals={bandTotal.cycles}
                        rows={group.rows}
                      />
                    )}
                  </section>
                  );
                })}
                {showTotals && grandTotals !== undefined && (
                  <TotalsCard
                    variant="grand"
                    label="Grand total"
                    qualifier={grandTotalQualifier}
                    cycles={visibleCycles}
                    totals={grandTotals}
                    rows={visibleRows}
                  />
                )}
                </>
              )}
            </div>

            <div className="hidden lg:block">
              {initialLoad ? (
                <TableWrap className={TABLE_SCROLL_AREA}>
                  {/* See `tableMinWidthPx` — the sum is DATA and cannot be a class. */}
                  <div style={{ minWidth: `${tableMinWidthPx(Math.max(1, visibleCycles.length))}px` }}>
                    <Table>
                      <RevenueTableHead cycles={visibleCycles.length === 0 ? [''] : visibleCycles} />
                      <TableSkeleton cycleCount={Math.max(1, visibleCycles.length)} />
                    </Table>
                  </div>
                </TableWrap>
              ) : groups.length === 0 ? (
                emptyState
              ) : (
                <TableWrap className={TABLE_SCROLL_AREA}>
                  <div style={{ minWidth: `${tableMinWidthPx(visibleCycles.length)}px` }}>
                    <Table>
                      <RevenueTableHead cycles={visibleCycles} />
                      {groups.map((group, groupIndex) => {
                        /*
                         * 🔴 **BY INDEX, MODULO — never by region.** See
                         * `BAND_TONES`: the bands are data, a fifth one must
                         * render without this file being edited, and a lookup
                         * keyed on the region would answer `undefined` for it.
                         * `?? BAND_TONES[0]` is unreachable arithmetic and is
                         * written anyway, because the alternative is a
                         * non-null assertion over an array somebody may later
                         * shorten.
                         */
                        const tone = bandTone(groupIndex);
                        /*
                         * `undefined` on three different servers-and-states —
                         * no `bandTotals` at all, a band the response did not
                         * total, and a band that appears in two runs — and all
                         * three mean the same thing to a reader: this band has
                         * no closing row. See `bandTotalFor`.
                         */
                        const bandTotal = bandTotalFor(group.key);
                        return (
                        <TBody key={group.key}>
                          <TR style={NO_ROW_RULE}>
                            {/*
                             * A `<th scope="row">` spanning the whole width: the
                             * workbook's bold band rule, and a real row header
                             * for a screen reader rather than a styled cell.
                             *
                             * ⚠️ **THE LABEL INSIDE IT IS STICKY.** The cell is
                             * the table's full width, so without this the band
                             * name scrolls off the moment somebody looks at a
                             * later cycle — and a band whose name is off screen
                             * is a currency the reader can no longer see. It
                             * sticks WITHIN its own cell, over that cell's own
                             * background, so nothing can show through it — the
                             * cell holds nothing else for it to overlap.
                             *
                             * ⚠️ **THE COLSPAN IS DERIVED**, because the
                             * separator columns count too and `1 + n * 4` no
                             * longer describes a row. See `bodyColumnCount`.
                             *
                             * 🔴 **ITS BOTTOM RULE IS AN INSET SHADOW, NOT A
                             * BORDER, AND ONLY FOR THE 240px UNDER THE FROZEN
                             * COLUMN.** A collapsed border at this boundary is
                             * painted at the grid line and belongs to the table,
                             * so the sticky company cell of the first block —
                             * which paints above the table's border layer —
                             * would cover it, leaving the band heading merging
                             * into that one cell while the rule ran on
                             * everywhere else. Drawn from inside the heading's
                             * OWN box, one pixel up, nothing below can reach it.
                             * `hairline-strong` because a band boundary should
                             * outrank the cell grid it introduces.
                             */}
                            <TH
                              scope="row"
                              colSpan={bodyColumnCount(visibleCycles.length)}
                              className={cx(
                                'font-semibold text-fg shadow-[inset_0_-1px_0_0_var(--hairline-strong)]',
                                tone.heading,
                              )}
                            >
                              <span className="sticky left-3 inline-flex items-baseline gap-2">
                                <span className="min-w-0 truncate">{group.label}</span>
                                {bandUnitOf(group, measuredInCurrency) !== null && (
                                  <span className="shrink-0 text-2xs font-normal text-fg-muted">
                                    {bandUnitOf(group, measuredInCurrency)}
                                  </span>
                                )}
                                <span className="shrink-0 text-2xs font-normal text-fg-muted tabular">
                                  {group.rows.length}{' '}
                                  {group.rows.length === 1 ? 'company' : 'companies'}
                                </span>
                              </span>
                            </TH>
                          </TR>
                          {group.rows.map((row, blockIndex) => (
                            <CompanyBlock
                              key={row.key}
                              row={row}
                              cycles={visibleCycles}
                              /*
                               * ⚠️ The stripe is the COMPANY's position within
                               * its band, not the row's position on the page:
                               * every band restarts plain under its own
                               * heading, which is what keeps the two readable
                               * as separate tables rather than as one striped
                               * run that happens to have headings in it.
                               */
                              tint={blockTint(tone, blockIndex)}
                              writer={writer}
                              onNotesSaved={applyNotes}
                            />
                          ))}
                          {/*
                           * The band's closing `TOTAL` row — inside this band's
                           * own `<tbody>`, because that is what it closes.
                           */}
                          {showTotals && bandTotal !== undefined && (
                            <TotalsRow
                              variant="band"
                              label="Total"
                              qualifier={group.label}
                              cycles={visibleCycles}
                              totals={bandTotal.cycles}
                              rows={group.rows}
                            />
                          )}
                        </TBody>
                        );
                      })}
                      {/*
                       * 🔴 **THE GRAND TOTAL IS ITS OWN `<tbody>`, NOT A
                       * `<tfoot>`, AND THAT IS A CONSTRAINT RATHER THAN A
                       * PREFERENCE.** `src/components/ui/Table.tsx` exports no
                       * `TFoot`, this app's rule is to build tables out of those
                       * primitives rather than hand-rolling cells beside them,
                       * and adding one is a change to a shared primitive that
                       * this pass does not own. A trailing `<tbody>` renders and
                       * reads identically here — the row still carries its own
                       * `<th scope="row">` — so the only thing given up is a
                       * semantic that nothing on this page consumes. **If a
                       * `TFoot` is ever added, this is its first caller.**
                       */}
                      {showTotals && grandTotals !== undefined && (
                        <TBody>
                          <TotalsRow
                            variant="grand"
                            label="Grand total"
                            qualifier={grandTotalQualifier}
                            cycles={visibleCycles}
                            totals={grandTotals}
                            rows={visibleRows}
                          />
                        </TBody>
                      )}
                    </Table>
                  </div>
                </TableWrap>
              )}
            </div>
          </div>

          {/*
           * ⚠️ **THE PAGER RENDERS ONLY WHERE THE SERVER SENT A `meta`.** The
           * response envelope for this endpoint was not published, so an
           * unpaginated body is a supported shape here — and a pager over a
           * whole answer is two dead buttons that imply there is more.
           */}
          {paginated && (
            <PanelFooter>
              <p className="m-0 text-center text-xs text-fg-muted tabular sm:text-left">
                Page {meta?.page ?? page} of {totalPages}
                {meta ? ` · ${meta.total} companies in total` : ''}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  className="flex-1 sm:flex-none"
                  disabled={page <= 1}
                  onClick={() => setPage((current) => current - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  className="flex-1 sm:flex-none"
                  disabled={page >= totalPages}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Next
                </Button>
              </div>
            </PanelFooter>
          )}
        </Panel>
      )}
    </InvoiceQuickEdit>
  );
}
